<script setup lang="ts">
import {
  useBillingStore,
  type BillingTermKind,
  type CatalogAddon,
  type CatalogOffering,
  type CatalogTemplate,
  type SimulationInput,
  type SimulationQuote,
  type SubscriptionSummary,
  type CouponPreview,
  type OrderResultRequiresTopup,
  type OrderResultPendingPayment,
  type OrderResultProvisioned,
} from '~/store/billing'
import {
  isPasswordCompliant,
  isVmPasswordCharsetValid,
  passwordStrength,
  type PasswordStrengthLevel,
} from '~/utils/password'
import { useVpcStore } from '~/store/vpc'
import { useImagesStore } from '~/store/images'
import { formatImageBytes } from '~/utils/images'
import { useUserStore } from '~/store/user'
import { getApiErrorBody, getApiErrorMessage } from '~/utils/apiError'
import { useRoute } from 'vue-router'
import type { AnalyticsItem } from '~/composables/useAnalytics'
import { addonsInSlot, type CheckoutAddonSlot } from '~/utils/checkout-addons'
import { quoteToAnalyticsItems, quoteAnalyticsValue } from '~/utils/checkout-analytics'
import { initialCreditCents, initialCreditInput } from '~/utils/checkout-credit'
import type {
  CheckoutProgressStep,
  CheckoutStepRequirement,
} from '~/composables/useCheckoutProgress'

/**
 * THE checkout. One implementation, two shells.
 *
 * `/configure` renders it publicly (no console chrome, no session) and
 * `/billing/checkout` renders it inside the dashboard for someone already
 * signed in. Everything else — configuring, pricing, signing up, paying,
 * waiting for the invoice to clear — happens here, on whichever page the
 * customer happens to be on.
 *
 * It used to be two pages with a hand-over between them: the public one priced
 * a server, took the customer's details, and then REDIRECTED to this one to
 * finish. That seam was the problem. It threw the customer out of the page they
 * were working in at the exact moment they had committed, it needed a whole
 * draft-persistence layer to carry the configuration across, and it left the
 * public page permanently one feature behind — no password field, no
 * bring-your-own image, no way to actually order. Once the visitor signs up in
 * place they HAVE a session, so there was never anything the second page could
 * do that this one could not.
 *
 * What is left of that design is the part that was right: the account-scoped
 * features are absent until they mean something. They are driven by DATA rather
 * than by a flag — no subscriptions means no billing picker, no VPCs means no
 * private networking, no images means no "my images" list — so a brand-new
 * account sees a simple page and a long-standing customer sees all of it,
 * without either case being special-cased.
 */
const props = defineProps<{
  /**
   * Rendered on the public route, so nobody is signed in yet and the identity
   * step is part of the flow. It changes WHEN we ask who is buying, and
   * nothing else: every other difference follows from the session and the data.
   */
  isPublic?: boolean
}>()

const billing = useBillingStore()
const labels = useBillingLabels()
const pricing = useCheckoutPricing()
const { locationLabel } = useDatacenterDisplay()
// `loading` is the composable's own, and every other caller that has a button
// worth disabling already binds it (HelpSlideover, MessengerHome,
// InvoicesSlideover). "Pay now" did not, so it sat inert for the ~1.15s a WHMCS
// call takes on the single most important click in the flow.
const { ssoToWhmcs, openUrlInNewTab, loading: openingInvoice } = useWhmcsSso()
const vpcStore = useVpcStore()
const userStore = useUserStore()
const analytics = useAnalytics()
const toast = useToast()
// The trial offer is read through the BFF resolver: the native app answers a
// relative `/api/…` from its own bundle (see `resolveNitroUrl`).
const { bffFetch } = useMwBff()
const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const { $mwApi } = useNuxtApp()
const { addListener, removeListener, connect: connectSocket } = useWebsocket()

// ── Catalog ───────────────────────────────────────────────────────────────
const { status: catStatus } = await useAsyncData(
  'billing-catalog-checkout',
  () => billing.fetchCatalog().catch(() => null),
  { server: true, lazy: false, default: () => null },
)
const loadingCatalog = computed(() => catStatus.value === 'pending' || billing.loadingCatalog)

/**
 * Whether anybody is signed in yet.
 *
 * Every difference between the two routes reduces to this plus the DATA it
 * gives access to. Nothing is keyed on `isPublic` except when to ask who is
 * buying — a signed-in customer opening `/configure` gets their subscriptions
 * and images exactly as they would in the console.
 */
const signedIn = computed(() => userStore.sessionStatus === 'authenticated' && !!userStore.user)

// ── Server locations ──────────────────────────────────────────────────────
/**
 * Read from the PUBLIC catalog endpoint, for everyone.
 *
 * `GET /datacenters` needs a session and answers with the datacenter entity,
 * `clusters` and all — hypervisor topology that has no business on a page an
 * anonymous visitor can open. `billing/catalog/locations` publishes exactly
 * what the picker needs, so both routes can use one code path instead of the
 * page having to know which of two endpoints it is allowed to call.
 */
const datacenters = ref<any[]>([])
const loadingDcs = ref(true)
async function fetchDatacenters() {
  loadingDcs.value = true
  try {
    const res = await ($mwApi as any).billingCatalogLocations()
    datacenters.value = res.data ?? []
  } catch (err) {
    console.error('failed to load server locations', err)
  } finally {
    loadingDcs.value = false
  }
}
await useAsyncData('checkout-datacenters', fetchDatacenters, { server: false })

// ── VPCs ──────────────────────────────────────────────────────────────────
const vpcs = ref<any[]>([])
const loadingVpcs = ref(true)
async function fetchVpcs() {
  // Guarded, not merely tolerant of failure: an anonymous visitor calling this
  // would take a 401 for nothing on every page load, and a page that fires
  // authenticated requests it knows will fail is how a real one gets lost in
  // the noise.
  if (!signedIn.value) {
    vpcs.value = []
    loadingVpcs.value = false
    return
  }
  loadingVpcs.value = true
  try {
    vpcs.value = (await vpcStore.getAllVpcs()) ?? []
  } catch {
    vpcs.value = []
  } finally {
    loadingVpcs.value = false
  }
}
await useAsyncData('checkout-vpcs', fetchVpcs, { server: false })

// ── Subscriptions for picker ──────────────────────────────────────────────
const availableSubscriptions = ref<SubscriptionSummary[]>([])
const loadingSubscriptions = ref(true)
async function fetchCheckoutSubscriptions() {
  if (!signedIn.value) {
    availableSubscriptions.value = []
    loadingSubscriptions.value = false
    return
  }
  loadingSubscriptions.value = true
  try {
    availableSubscriptions.value = await billing.fetchSubscriptionsForCheckout()
  } catch {
    availableSubscriptions.value = []
  } finally {
    loadingSubscriptions.value = false
  }
}
await useAsyncData('checkout-subscriptions', fetchCheckoutSubscriptions, {
  server: false,
})

// ── The customer's own images ─────────────────────────────────────────────
/**
 * Loaded client-side and tolerant of failure: this is one tab of an order flow,
 * and an image list that will not load must not stop somebody buying a server
 * with a catalog OS.
 *
 * Loaded HERE, above the configurator, because the configurator takes it as a
 * source — an image states its own disk minimum, so it feeds the same
 * auto-raise and the same blockers a catalog template does.
 */
const imagesStore = useImagesStore()
// Not awaited: a top-level await in this setup would detach every lifecycle
// hook registered after it. See the images page for the full reasoning.
const { data: clientImagesRaw, refresh: refreshClientImages } = useAsyncData(
  'checkout-client-images',
  async () => {
    if (!signedIn.value) return []
    try {
      return await imagesStore.fetchImages()
    } catch {
      return []
    }
  },
  { server: true, lazy: true, default: () => [] },
)

const clientImageOptions = computed(() =>
  (clientImagesRaw.value ?? [])
    .filter((i) => i.kind === 'disk_image' && i.status === 'ready')
    .map((i) => ({
      id: i.id,
      name: i.display_name || i.original_filename,
      minDiskGb: i.min_disk_gb ?? null,
      hasCloudInit: i.has_cloud_init,
      hasCustomCloudInit: !!i.has_cloud_init && !!i.cloud_init_user_data_format,
      sizeLabel: formatImageBytes(i.size_bytes),
    })),
)

// ── Subscription selection ────────────────────────────────────────────────
// 'new' = create new subscription | string = existing subscription ID
const trialCode = ref(typeof route.query.trial === 'string' ? route.query.trial.toUpperCase() : '')
const trialOffer = ref<import('~/generated-api-client').PublicTrialDto | null>(null)
const trialLoading = ref(false)
const trialError = ref('')
const trialAccepted = ref(false)
const trialAccountMismatch = ref(false)
async function switchTrialAccount() {
  await userStore.logout()
  window.location.assign('/configure?trial=' + encodeURIComponent(trialCode.value))
}
const trialActive = computed(() => !!trialCode.value)
const paidCheckoutLocation = computed(() => {
  const { trial: _trial, ...query } = route.query
  return { path: route.path, query }
})
const trialStatusMessage = computed(() => {
  if (trialLoading.value) return t('trial.loading')
  if (trialError.value) return trialError.value
  // The offer is fetched after mount. SSR and hydration must describe the
  // unresolved offer as loading, never infer expiry from a missing response.
  if (!trialOffer.value) return t('trial.loading')
  return trialOffer.value.state === 'open' ? null : t('trial.state.' + trialOffer.value.state)
})
const trialAllows = (type: string) =>
  !trialActive.value ||
  (!!trialOffer.value?.budget_micro_cents && trialOffer.value.allowed_addon_types.includes(type))
const selectedSubscriptionId = ref<'new' | string>('new')
const subscriptionSelectionPending = ref(false)

// Pre-select if ?sub=xxx is in the query
watch(
  availableSubscriptions,
  (subs) => {
    const querySub = route.query.sub as string | undefined
    if (querySub && subs.find((s) => s.id === querySub)) {
      selectedSubscriptionId.value = querySub
    }
  },
  { immediate: true },
)

const selectedSubscription = computed<SubscriptionSummary | null>(
  () => availableSubscriptions.value.find((s) => s.id === selectedSubscriptionId.value) ?? null,
)

// ── Configuration ─────────────────────────────────────────────────────────
/**
 * The whole product configuration — location, cycle, plan, OS, storage,
 * connectivity, add-ons and the live quote — lives in `useCheckoutConfigurator`
 * and is shared with the PUBLIC configurator at `/configure`.
 *
 * What stays on this page is everything only an account can answer: which
 * subscription to bill, a coupon, the balance, the password, and placing the
 * order. That split is the whole reason the public page can exist without
 * being this page with a signed-in flag threaded through it.
 *
 * Nothing is pre-selected: location, plan and OS are all the customer's to
 * choose. The cost is that no quote exists until all three are set, so the
 * summary leads with the checklist of what is still missing instead of a price.
 */
const selectedSubscriptionTerm = computed<BillingTermKind | null>(
  () => selectedSubscription.value?.term?.kind ?? null,
)

/**
 * Adding to an EXISTING subscription prices at that subscription's cycle: its
 * term is a fact about it, not a choice being made here.
 */
const termOverride = computed<BillingTermKind | null>(() =>
  trialOffer.value
    ? (trialOffer.value.term as BillingTermKind)
    : selectedSubscriptionId.value === 'new'
      ? null
      : (selectedSubscriptionTerm.value ?? 'ON_DEMAND'),
)

const configurator = useCheckoutConfigurator({
  datacenters,
  clientImages: clientImageOptions,
  vpcs,
  termOverride,
})

const {
  selectedDatacenterId,
  selectedOfferingCode,
  selectedTerm,
  osTab,
  selectedClientImageId,
  selectedTemplateCode,
  connectivityMode,
  selectedVpcId,
  mainDiskExtraGb,
  extraDisks,
  addonQuantities,
  checkoutTerm,
  deployableDatacenters,
  selectedDatacenter,
  ipAvailability,
  vpcsInDatacenter,
  selectedVpcName,
  computeOfferings: catalogComputeOfferings,
  selectedOffering,
  baseDiskGb,
  diskStep,
  minExtraDiskGb,
  mainDiskTotalGb,
  totalStorageGb,
  selectedTemplate,
  selectedClientImage,
  osChoice,
  osDisplayName,
  osMinDiskGb,
  osMinRamMb,
  osMinVcpu,
  minMainExtraGb,
  planBelowOsMin,
  osRequirementsUnmet,
  offeringMeetsOsMin,
  autoAdjustments,
  isAddonVisible,
  addonIpCap,
  isToggleAddon,
  incAddon,
  decAddon,
  toggleBoolAddon,
  rateForAddon,
  vcpuAddonTotalRate,
  hasVisibleAddons,
  planMonthlyPrice,
  planHourlyPrice,
  planReferencePrice,
  planSavingsPct,
  quote,
  simulating,
  simulationError,
  onDemandMonthlyMicroCents,
  buildQuoteInput,
  configurationBlockers,
  datacenterDone,
  planDone,
  osDone,
  connectivityDone,
} = configurator
const computeOfferings = computed(() =>
  trialActive.value
    ? catalogComputeOfferings.value.filter((p) =>
        trialOffer.value?.plans.some((x) => x.code === p.code),
      )
    : catalogComputeOfferings.value,
)
const trialUsage = computed(() => {
  if (!trialOffer.value || !quote.value) return 0n
  const span = (new Date(trialOffer.value.trial_ends_at).getTime() - Date.now()) / 3600000
  const hours = Math.ceil(span)
  const cap =
    billing.catalog?.terms.find((x) => x.kind === trialOffer.value!.term)?.commitment_hours ?? hours
  return (
    BigInt(quote.value.total_hourly_rate_micro_cents) *
    BigInt(Math.max(0, Math.min(hours, Math.max(cap ?? hours, Math.floor(span)))))
  )
})
const trialRenewal = ref<{
  gross_micro_cents: string
  discount_micro_cents: string
  net_micro_cents: string
} | null>(null)
const trialRenewalLoading = ref(false)
const trialRenewalError = ref(false)
const trialGrossRenewal = computed(
  () =>
    quote.value?.total_cycle_micro_cents ??
    trialOffer.value?.plans.find((p) => p.code === selectedOfferingCode.value)
      ?.renewal_micro_cents ??
    trialOffer.value?.plans[0]?.renewal_micro_cents ??
    '0',
)
let trialRenewalSequence = 0
watch([trialOffer, trialGrossRenewal], async () => {
  const sequence = ++trialRenewalSequence
  trialRenewal.value = null
  trialRenewalError.value = false
  trialAccepted.value = false
  trialRenewalLoading.value = false
  if (!trialOffer.value?.has_renewal_coupon || trialOffer.value.state !== 'open') return
  trialRenewalLoading.value = true
  try {
    const result = await bffFetch<typeof trialRenewal.value>(
      '/billing/trials/offers/' + encodeURIComponent(trialCode.value) + '/renewal',
      { query: { total_micro_cents: trialGrossRenewal.value } },
    )
    if (sequence === trialRenewalSequence) trialRenewal.value = result
  } catch {
    if (sequence === trialRenewalSequence) trialRenewalError.value = true
  } finally {
    if (sequence === trialRenewalSequence) trialRenewalLoading.value = false
  }
})
const trialDescription = computed(() =>
  trialOffer.value
    ? t('trial.terms', {
        date: new Date(trialOffer.value.trial_ends_at).toLocaleString(locale.value),
        price: billing.formatMicroCents(
          trialRenewal.value?.net_micro_cents ?? trialGrossRenewal.value,
        ),
        term: termDisplayName.value,
        hours: trialOffer.value.grace_hours,
      })
    : '',
)
const trialSummary = computed(() => trialActive.value && trialOffer.value ? {
  endsAt: trialOffer.value.trial_ends_at,
  renewalMicroCents: trialRenewalLoading.value || trialRenewalError.value
    ? null : trialRenewal.value?.net_micro_cents ?? trialGrossRenewal.value,
  termLabel: termDisplayName.value,
  creditMicroCents: trialOffer.value.budget_micro_cents,
  usedMicroCents: trialUsage.value.toString(),
} : null)

async function resolveTrial() {
  if (!trialCode.value) return
  trialLoading.value = true
  trialError.value = ''
  try {
    trialOffer.value = await bffFetch<NonNullable<typeof trialOffer.value>>(
      '/billing/trials/offers/' + encodeURIComponent(trialCode.value),
    )
    selectedSubscriptionId.value = 'new'
    selectedTerm.value = trialOffer.value!.term as BillingTermKind
    if (!trialOffer.value!.plans.some((p) => p.code === selectedOfferingCode.value))
      selectedOfferingCode.value = trialOffer.value!.plans[0]?.code ?? null
    if (!trialAllows('EXTRA_DISK')) extraDisks.value = []
    for (const addon of billing.catalog?.addons ?? [])
      if (!trialAllows(addon.addon_type)) addonQuantities.value[addon.code] = 0
    couponCode.value = ''
  } catch (e) {
    trialError.value = t('trial.unavailable')
  } finally {
    trialLoading.value = false
  }
}

// An on-demand order buys prepaid credit; hourly usage spends it after payment.
const isNewOnDemand = computed(
  () => selectedSubscriptionId.value === 'new' && checkoutTerm.value === 'ON_DEMAND',
)
const initialRechargeMinimumCents = computed(() => {
  const minimums = billing.catalog?.recharge_minimums
  return minimums?.on_demand_initial_cents ?? minimums?.on_demand_cents ?? null
})
const initialRechargeAmount = ref<string | number>('')
watch(
  initialRechargeMinimumCents,
  (minimum, previous) => {
    if (
      minimum &&
      (!initialRechargeAmount.value ||
        (previous && initialRechargeAmount.value === initialCreditInput(previous)))
    ) {
      initialRechargeAmount.value = initialCreditInput(minimum)
    }
  },
  { immediate: true },
)
const initialRechargeCents = computed(() => initialCreditCents(initialRechargeAmount.value))
const initialRechargeError = computed(() => {
  if (!isNewOnDemand.value) return null
  const minimum = initialRechargeMinimumCents.value
  if (!minimum) return t('billing.page.checkout.recharge.unavailable')
  if (!initialRechargeCents.value) return t('billing.page.checkout.recharge.invalid')
  if (BigInt(initialRechargeCents.value) < BigInt(minimum)) {
    return t('billing.page.checkout.recharge.minimum', {
      amount: billing.formatCents(minimum),
    })
  }
  return null
})
const orderInitialCreditCents = computed(() =>
  isNewOnDemand.value ? initialRechargeCents.value : null,
)

/**
 * A coupon can only be previewed for a signed-in account —
 * `POST /checkout/v2/coupons/preview` is authenticated and is scoped to the
 * account, since a private coupon is granted per customer. Showing the field
 * to somebody with no session offers a control that can only 401.
 */
const couponAllowed = computed(
  () =>
    !trialActive.value &&
    signedIn.value &&
    selectedSubscriptionId.value === 'new' &&
    checkoutTerm.value !== 'ON_DEMAND',
)

// Keep the cycle picker showing the subscription's own term when one is being
// added to, so the control and the price agree about what is being bought.
watch(
  [selectedSubscriptionId, selectedSubscriptionTerm],
  ([subscriptionId, term]) => {
    if (subscriptionId !== 'new') selectedTerm.value = term ?? 'ON_DEMAND'
  },
  { immediate: true },
)

// ── Coupon ────────────────────────────────────────────────────────────────
// Account-scoped: `POST /checkout/v2/coupons/preview` needs a session, so this
// is one of the things the public configurator deliberately cannot offer.
const couponCode = ref('')
const couponPreview = ref<CouponPreview | null>(null)
const couponLoading = ref(false)
const couponError = ref<string | null>(null)
let couponPreviewTimer: ReturnType<typeof setTimeout> | null = null
let couponPreviewSeq = 0

const normalizedCouponCode = computed(() => couponCode.value.trim().toUpperCase())
// The order buys ONE service; its cycle total is that service's renewal
// share, which is what the coupon discounts on every renewal.
const couponServiceTotalMicroCents = computed(() => {
  if (!couponAllowed.value || !quote.value?.total_cycle_micro_cents) return null
  return quote.value.total_cycle_micro_cents
})
const activeCouponPreview = computed(() =>
  couponAllowed.value && normalizedCouponCode.value && couponPreview.value?.valid
    ? couponPreview.value
    : null,
)

function resetCouponPreview(clearCode = false) {
  if (couponPreviewTimer) {
    clearTimeout(couponPreviewTimer)
    couponPreviewTimer = null
  }
  couponPreviewSeq++
  couponInFlightCode = null
  couponSettledKey = null
  couponPreview.value = null
  couponError.value = null
  couponLoading.value = false
  if (clearCode) couponCode.value = ''
}

/** The code the request in flight is asking about. */
let couponInFlightCode: string | null = null
/**
 * The (code, total) the server has already ruled on, and whose ruling is on
 * screen. Includes the total because validity depends on it — a coupon with a
 * minimum spend flips as the configuration changes.
 */
let couponSettledKey: string | null = null

async function refreshCouponPreview() {
  if (!couponAllowed.value) {
    resetCouponPreview(true)
    return
  }
  const code = normalizedCouponCode.value
  const total = couponServiceTotalMicroCents.value
  // This IS the scheduled work, so cancel the schedule. Without it a pending
  // debounce fired again straight after an Apply click, wiping the answer that
  // had just arrived and re-entering the loading state for the same code.
  if (couponPreviewTimer) {
    clearTimeout(couponPreviewTimer)
    couponPreviewTimer = null
  }
  // Apply is reachable three ways — the debounce on typing, the field's blur,
  // and the button itself — and a mouse click fires blur THEN click, so one
  // deliberate Apply asked the server two or three times for the same code and
  // the panel flickered result → blank → spinner → result. A second request for
  // the code already in flight adds nothing; an Apply after it settles is a
  // deliberate retry and still runs.
  if (couponLoading.value && couponInFlightCode === code) return
  // The field previews itself as it is typed, so by the time Apply is reached
  // the verdict is usually already on screen — and asking again returns the
  // same answer while blanking the panel and re-running the spinner, which is
  // the flicker this button was blamed for. A server verdict is a fact; only a
  // new code, a new total, or a request that never got an answer (a transport
  // failure leaves no preview) is worth another round trip.
  if (couponPreview.value && couponSettledKey === `${code}|${total}`) return
  couponError.value = null
  couponPreview.value = null
  if (!code || !total) return
  const seq = ++couponPreviewSeq
  couponInFlightCode = code
  couponLoading.value = true
  try {
    const preview = await billing.previewCoupon({
      coupon_code: code,
      service_total_micro_cents: total,
    })
    if (seq === couponPreviewSeq) {
      couponPreview.value = preview
      couponSettledKey = `${code}|${total}`
    }
  } catch (err: any) {
    if (seq === couponPreviewSeq) {
      couponError.value = err?.response?.data?.message ?? err.message
    }
  } finally {
    if (seq === couponPreviewSeq) {
      couponLoading.value = false
      couponInFlightCode = null
    }
  }
}

function scheduleCouponPreview() {
  if (couponPreviewTimer) clearTimeout(couponPreviewTimer)
  couponPreviewTimer = setTimeout(refreshCouponPreview, 350)
}

watch([normalizedCouponCode, couponServiceTotalMicroCents, couponAllowed], () => {
  if (!couponAllowed.value) {
    resetCouponPreview(true)
    return
  }
  if (!normalizedCouponCode.value) {
    resetCouponPreview()
    return
  }
  scheduleCouponPreview()
})

// ── Password ──────────────────────────────────────────────────────────────
const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const requiresPassword = computed(() => selectedTemplate.value?.choose_password === 1)

/**
 * Password policy comes from `~/utils/password` — the same module the account
 * signup uses — so the checklist under the field and the button gate can never
 * disagree. This page used to score the password 0–8 privately and block at
 * `score >= 5`, showing only a bar: the customer was told "not strong enough"
 * with no way to learn what was missing.
 */
const passwordMeter = computed(() => passwordStrength(password.value))
// The instance's own password: the account rules plus visible ASCII only,
// which the API enforces too (IsVmPassword).
const isPasswordStrong = computed(() => isPasswordCompliant(password.value, 'vm'))
// A refused character is a different problem from a weak password, and says so:
// "set a strong password" sent people adding characters to a password whose
// only fault was one they had to remove.
const passwordCharsetValid = computed(() => isVmPasswordCharsetValid(password.value))

// Same palette the account signup meter uses.
const STRENGTH_BAR: Record<PasswordStrengthLevel, string> = {
  weak: 'bg-red-500',
  fair: 'bg-orange-500',
  good: 'bg-yellow-500',
  strong: 'bg-green-500',
}
const STRENGTH_TEXT: Record<PasswordStrengthLevel, string> = {
  weak: 'text-red-600 dark:text-red-400',
  fair: 'text-orange-600 dark:text-orange-400',
  good: 'text-yellow-600 dark:text-yellow-400',
  strong: 'text-green-600 dark:text-green-400',
}
const passwordsMatch = computed(
  () => password.value === confirmPassword.value && password.value.length > 0,
)

watch(selectedTemplateCode, () => {
  password.value = ''
  confirmPassword.value = ''
})

// ── Balance check ─────────────────────────────────────────────────────────
/**
 * For an existing subscription, compute if the balance covers the prorata cost.
 * The server does this authoritatively when we call createOrder, but we also
 * pre-compute it client-side so the UI can show the right CTA before the user clicks.
 */
const balanceCoversCost = computed((): boolean | null => {
  if (selectedSubscriptionId.value === 'new') return null
  const sub = selectedSubscription.value
  if (!sub?.balance || !quote.value?.total_hourly_rate_micro_cents) return null
  // For on-demand: just check usable > 0 (Y check is server-side)
  // For committed: rough check — usable >= prorata (server recomputes)
  return BigInt(sub.balance.usable_micro_cents) > 0n
})

/**
 * What an instance added to an existing COMMITTED subscription is charged
 * NOW: the hours left in the current cycle (capped at the term) at the quoted
 * rate. Estimated client-side from the subscription's cycle end; the server
 * recomputes it when the order is placed. The instance then renews with the
 * subscription at the full period price, which the summary states beside it.
 */
const attachProrataHours = computed<number | null>(() => {
  if (selectedSubscriptionId.value === 'new') return null
  const q = quote.value
  const endsAt = selectedSubscription.value?.current_cycle_ends_at
  if (!q?.cycle_hours || !endsAt) return null
  const hoursLeft = Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 3_600_000))
  return Math.min(hoursLeft, q.cycle_hours)
})
const attachProrataEstimate = computed<string | null>(() => {
  const hours = attachProrataHours.value
  const q = quote.value
  if (hours == null || !q?.total_hourly_rate_micro_cents) return null
  return (BigInt(q.total_hourly_rate_micro_cents) * BigInt(hours)).toString()
})

// ── Sending the order at most once ────────────────────────────────────────
/**
 * One key per ORDER INTENT, not per click.
 *
 * The key covers the request body as well as the route, so a retry carrying the
 * same key but a DIFFERENT configuration is refused outright rather than
 * answered with the old order — which is right, and means the key has to be
 * retired whenever the customer changes what they are buying. The draft
 * watcher below already fires on exactly that, so it is what clears this.
 *
 * The result: a double submit replays, and an edited retry gets a fresh key.
 */
const orderIntentKey = ref<string | null>(null)

function orderIntentKeyForSend(): string {
  if (!orderIntentKey.value) {
    orderIntentKey.value =
      globalThis.crypto?.randomUUID?.() ??
      `intent-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
  return orderIntentKey.value
}

// ── Order placement ───────────────────────────────────────────────────────
const placing = ref(false)

/**
 * Outcome state after a successful createOrder call that needs user action.
 * null = no order placed yet (initial) or provisioned (redirected).
 */
const pendingInvoice = ref<{
  orderId: string
  invoiceExternalId: string
  payUrl: string | null
  totalCents: string
  initialCreditCents: string | null
  grossCents: string | null
  discountCents: string | null
  /** Subscription balance the server took off the charge (cents). */
  balanceAppliedCents: string | null
  /** What the minimum recharge added above the net charge (cents); stays as credit. */
  minimumRechargeCreditCents: string | null
  /** Add-to-existing: hours charged, the renewal date and the full-period price. */
  chargedHours: number | null
  cycleEndsAt: string | null
  termCents: string | null
  coupon: CouponPreview | null
  expiresAt: string | null
} | null>(null)

const requiresTopupState = ref<OrderResultRequiresTopup | null>(null)
const topupGenerating = ref(false)

/** Top-up recharge invoice awaiting payment (the balance path's equivalent). */
const pendingTopup = ref<{
  invoiceExternalId: string
  payUrl: string | null
  totalCents: string
} | null>(null)

/**
 * Everything still preventing the order, in user-facing terms. The CTA used to
 * combine these conditions into a silently-disabled button; now each unmet
 * requirement is listed next to it. `canPlaceOrder` derives from this list so
 * the button and the checklist can never disagree.
 *
 * The configuration half comes from the shared configurator, so the public page
 * reports the identical wording for the identical condition. What is added here
 * is what only a signed-in checkout can be blocked BY.
 */
const orderBlockers = computed<{ key: string; label: string }[]>(() => {
  const blockers = [...configurationBlockers.value]
  if (trialActive.value) {
    if (!trialOffer.value || trialOffer.value.state !== 'open' || trialLoading.value)
      blockers.push({ key: 'trial', label: t('trial.unavailable') })
    if (trialRenewalLoading.value || trialRenewalError.value)
      blockers.push({ key: 'trial', label: t('trial.renewalLoading') })
    if (!trialAccepted.value) blockers.push({ key: 'trialConsent', label: t('trial.acceptRequired') })
    if (
      trialOffer.value?.budget_micro_cents &&
      trialUsage.value > BigInt(trialOffer.value.budget_micro_cents)
    )
      blockers.push({ key: 'trial', label: t('trial.overBudget') })
    const types: Record<string, string> = {
      main_disk_expansion: 'MAIN_DISK_EXPANSION',
      extra_disk: 'EXTRA_DISK',
      extra_ipv4: 'EXTRA_IPV4',
      extra_ipv6: 'EXTRA_IPV6',
    }
    if (
      quote.value?.line_items.some(
        (line) =>
          line.kind !== 'compute' &&
          BigInt(line.hourly_subtotal_micro_cents) > 0n &&
          !trialAllows(line.addon_type ?? types[line.kind] ?? ''),
      )
    )
      blockers.push({ key: 'trial', label: t('trial.configNotAllowed') })
  }
  if (initialRechargeError.value)
    blockers.push({
      key: 'initialRecharge',
      label: initialRechargeError.value,
    })
  // Who is buying. Last in the list because it is last in the flow — the
  // price has been on screen the whole way down by the time it is asked.
  if (!signedIn.value)
    blockers.push({
      key: 'signIn',
      label: t('billing.page.checkout.blockers.signIn'),
    })
  if (subscriptionSelectionPending.value)
    blockers.push({
      key: 'subscription',
      label: t('billing.page.checkout.blockers.subscription'),
    })
  if (requiresPassword.value && !isPasswordStrong.value)
    blockers.push({
      key: 'password',
      label: passwordCharsetValid.value
        ? t('billing.page.checkout.blockers.password')
        : t('billing.page.checkout.blockers.passwordCharacters'),
    })
  else if (requiresPassword.value && !passwordsMatch.value)
    blockers.push({
      key: 'passwordMatch',
      label: t('billing.page.checkout.blockers.passwordMatch'),
    })
  // A coupon blocks the order only while the answer is still UNKNOWN. It used
  // to block on `!valid` too, which made a typo in an optional field the end of
  // the checkout: an expired code, a code for another product, or a coupon
  // preview that simply failed to reach the server left "Confirm and pay"
  // permanently refusing, with the reason filtered out of the blocker list
  // (`HANDLED_HERE`) so nothing on screen connected the two. The remedy for a
  // code we will not honour is to say we are not honouring it — which the
  // status line does — not to refuse the sale.
  //
  // Loading still blocks, and should: it is brief, it resolves itself, and it
  // stops an order going out at full price in the instant before a valid
  // discount lands.
  if (couponAllowed.value && normalizedCouponCode.value && couponLoading.value)
    blockers.push({
      key: 'coupon',
      label: t('billing.page.checkout.blockers.couponChecking'),
    })
  return blockers
})

/*
 * What the assistant and the messenger can see about this order.
 *
 * One object, three uses: the assistant answers about the ACTUAL configuration
 * instead of in generalities, Home swaps its subtitle and promotes the human
 * channel above the assistant when there is a blocker the customer cannot
 * self-serve, and an escalated conversation carries it to the agent.
 *
 * Only what is on screen — no ids the customer has never seen, no money the
 * quote has not settled.
 */
defineAiContext(() => {
  const entities: AiVisibleEntity[] = [
    {
      type: 'checkout',
      datacenter: datacenters.value.find((d) => d.id === selectedDatacenterId.value)?.name ?? null,
      plan: selectedOffering.value?.name ?? null,
      term: selectedTerm.value,
      os: selectedTemplate.value?.name ?? selectedClientImage.value?.name ?? null,
      subscription: selectedSubscriptionId.value === 'new' ? 'new' : 'existing',
    },
  ]
  // Every blocker is useful CONTEXT for the assistant — it can explain what is
  // still missing.
  for (const b of orderBlockers.value) {
    entities.push({ type: 'checkout_blocker', blocker: b.label, key: b.key })
  }

  /*
   * A FAILURE is a much narrower thing, and only a failure promotes the human
   * channel on the messenger's home screen.
   *
   * Most of `orderBlockers` is just an unfinished form — "choose a datacenter",
   * "choose a plan". Treating those as failures put "Falar com o comercial"
   * above the assistant for every single visitor who had not filled the form
   * yet, which is both noise and a bad first impression of the product.
   *
   * These two are different: the customer has done their part and the platform
   * said no. That is when a person is worth offering.
   */
  const zeroRate = orderBlockers.value.some((b) => b.key === 'zeroRate')
  if (requiresTopupState.value || zeroRate) {
    entities.push({
      type: 'checkout_failure',
      reason: requiresTopupState.value ? 'requires_topup' : 'zero_rate',
    })
  }
  return entities
})

const canPlaceOrder = computed(() => orderBlockers.value.length === 0 && !!quote.value)
// Consent belongs to the final review. The configurator may open that review
// as soon as the configuration and account are ready; submission still requires it.
const pageOrderBlockers = computed(() => orderBlockers.value.filter(b => b.key !== 'trialConsent'))
const canContinueOrder = computed(() => pageOrderBlockers.value.length === 0 && !!quote.value)

function buildSimulationInput(): SimulationInput {
  return buildQuoteInput(checkoutTerm.value)
}

// ── GA4 / Microsoft Ads: funnel + purchase ────────────────────────────────
// Item shaping is shared with the public configurator so the same server is not
// reported as two different products depending on where the customer started.
const analyticsItems = (): AnalyticsItem[] => quoteToAnalyticsItems(quote.value)
const analyticsValue = (): number => quoteAnalyticsValue(quote.value)

/**
 * begin_checkout — once, when the funnel first has a priced configuration.
 *
 * One page, one event. This used to need a guard against the hand-over firing
 * it a second time; with the two routes rendering the same component and the
 * customer never navigating mid-purchase, there is nothing to dedupe against.
 */
const begunCheckout = ref(false)
watch(
  () => quote.value?.offering_code,
  (code) => {
    if (!code || begunCheckout.value) return
    begunCheckout.value = true
    analytics.trackBeginCheckout(analyticsItems(), analyticsValue())
  },
  { immediate: true },
)

// add_to_cart — whenever the selected plan changes. Nothing is pre-selected, so
// every change here is a deliberate choice and needs no guard.
watch(
  () => selectedOffering.value?.code,
  (code) => {
    if (code) analytics.trackAddToCart(analyticsItems(), analyticsValue())
  },
)

async function placeOrder() {
  // Both CTAs bind `:loading="placing"` and a loading UButton is a disabled
  // one — but the review step adds a third call site and an Enter key, and one
  // line here is cheaper than trusting three call sites to stay careful.
  if (placing.value) return
  if (!canPlaceOrder.value || !osChoice.value) return
  analytics.trackAddPaymentInfo(analyticsItems(), analyticsValue())
  placing.value = true
  pendingInvoice.value = null
  requiresTopupState.value = null
  try {
    const result = await billing.createOrder({
      simulation: buildSimulationInput(),
      ...(trialActive.value ? { trial_code: trialCode.value } : {}),
      target_subscription_id:
        selectedSubscriptionId.value !== 'new' ? selectedSubscriptionId.value : undefined,
      ...(isNewOnDemand.value && initialRechargeCents.value
        ? { initial_credit_cents: initialRechargeCents.value }
        : {}),
      ...(requiresPassword.value ? { vm_password: password.value } : {}),
      ...(couponAllowed.value && activeCouponPreview.value
        ? { coupon_code: normalizedCouponCode.value }
        : {}),
      idempotency_key: orderIntentKeyForSend(),
    })

    // The purchase is made: the configuration has become an order, and a draft
    // that outlived it would re-apply itself over the next thing the customer
    // configures in this tab.
    if (result.status === 'provisioned' || result.status === 'pending_payment') {
      draft.clear()
    }

    if (result.status === 'provisioned') {
      // Order placed = the conversion. Enhanced Conversions: set hashed user
      // data right before purchase. Deduped with the server MP event by orderId.
      //
      // `getUser` IS the client — it returns `ClientDetailsDto`. This read used
      // to be `getUser?.client`, which is always undefined, so `setUserData`
      // returned at its own null guard and every purchase was reported with no
      // user data at all. Nothing failed and nothing logged; the only symptom
      // was weaker ad attribution.
      analytics.setUserData(userStore.getUser as any)
      analytics.trackPurchase({
        transaction_id: result.orderId,
        value: microCentsToMajor(result.quote?.total_cycle_micro_cents),
        items: analyticsItems(),
      })
      // Balance was enough — resource added immediately
      toast.add({
        title: t('billing.page.checkout.toast.instanceAdded'),
        description: t('billing.page.checkout.toast.instanceAddedDesc'),
        color: 'success',
      })
      commitOpen.value = false
      router.push('/billing')
      return
    }

    if (result.status === 'pending_payment') {
      // Order placed = the conversion (NOT payment — that finalizes in WHMCS).
      // Deduped with the server MP event by orderId.
      analytics.setUserData(userStore.getUser as any)
      analytics.trackPurchase({
        transaction_id: result.orderId,
        value: centsToMajor(result.totalCents),
        items: analyticsItems(),
        coupon: normalizedCouponCode.value || undefined,
      })
      // Survive a reload. The order is real and unpaid from this moment, and
      // the customer has every reason to leave the tab — a boleto is paid at a
      // bank, and often the next day. Without this they came back to a checkout
      // that looked untouched, with no way to reach the invoice they had just
      // committed to. See `rememberPendingOrder`.
      rememberPendingOrder(result.orderId)
      // Invoice issued — hand over to the waiting panel, which owns the tab,
      // the polling and the "I already paid" nudge from here on.
      pendingInvoice.value = {
        orderId: result.orderId,
        invoiceExternalId: result.invoiceExternalId,
        payUrl: result.payUrl ?? null,
        totalCents: result.totalCents,
        initialCreditCents: isNewOnDemand.value ? result.totalCents : null,
        grossCents: result.grossCents ?? null,
        discountCents: result.discountCents ?? null,
        balanceAppliedCents: result.balanceAppliedCents ?? null,
        minimumRechargeCreditCents: result.minimumRechargeCreditCents ?? null,
        chargedHours: result.chargedHours ?? null,
        cycleEndsAt: result.cycleEndsAt ?? null,
        termCents: result.termCents ?? null,
        coupon: result.coupon ?? null,
        expiresAt: null,
      }
      // Deliberately NOT opening `result.payUrl` here. It would run two
      // `await`s after the click, so Safari and Firefox are entitled to block
      // it as un-gestured — and when it is NOT blocked it rips focus to a WHMCS
      // invoice from behind a panel the customer never got to read. The panel's
      // own "Pay now" is a real gesture and goes through `openInvoicePage()`,
      // which prefers a proper SSO hop over the raw URL anyway.
      // Same surface, next step — not a second dialog over the first.
      openPaymentWait('order')
      return
    }

    if (result.status === 'requires_topup') {
      // Not enough balance. The top-up panel lives in the summary sidebar, so
      // the sheet has to get out of the way for it to be reachable at all.
      // (Unreachable on the guest path: a new subscription is always invoiced.)
      requiresTopupState.value = result
      commitOpen.value = false
      progress.goTo('checkout-summary')
    }
  } catch (err: any) {
    const body = getApiErrorBody(err)
    trialAccountMismatch.value = body.code === 'TRIAL_OFFER_NOT_FOR_THIS_ACCOUNT'
    const trialMessages: Record<string, string> = {
      TRIAL_OFFER_NOT_FOR_THIS_ACCOUNT: 'accountMismatch',
      TRIAL_OFFER_NOT_OPEN: 'unavailable',
      TRIAL_ALREADY_ACTIVE: 'alreadyActive',
      TRIAL_CONFIG_NOT_ALLOWED: 'configNotAllowed',
      TRIAL_BUDGET_EXCEEDED: 'overBudget',
    }
    const description =
      body.code && trialMessages[body.code]
        ? t('trial.' + trialMessages[body.code])
        : getApiErrorMessage(err)
    if (trialAccountMismatch.value) commitOpen.value = false
    // Twice, deliberately: the toast for someone looking at the page, and the
    // sheet's own alert for someone looking at the sheet — which is over the
    // toast and would otherwise show a button that did nothing.
    orderError.value = description
    toast.add({
      title: t('billing.page.checkout.toast.orderError'),
      description,
      color: 'error',
    })
  } finally {
    placing.value = false
  }
}

// ── An order that outlives the page ───────────────────────────────────────
/**
 * The one thing worth writing down about a placed order: its id.
 *
 * `pendingInvoice` is in memory, so a reload — or a customer who paid a boleto
 * at the bank and came back the next morning — used to find a checkout that
 * looked untouched, with nothing pointing at the invoice they had just
 * committed to. The order is not lost (it is in `/billing`, and WHMCS emailed
 * the invoice), but the page that took the decision offered no way back to it.
 *
 * Tab-scoped, beside the draft and for the same reasons — and the draft is
 * cleared at exactly the moment this is written, so the two never coexist.
 * Nothing but the id is stored: everything else is re-read from
 * `GET /checkout/v2/orders/:id/payment-state`, which already returns the
 * invoice alongside the state.
 */
const PENDING_ORDER_KEY = 'mw_checkout_pending_order'

function rememberPendingOrder(orderId: string) {
  try {
    sessionStorage.setItem(PENDING_ORDER_KEY, orderId)
  } catch {
    // Private mode. Costs the customer the shortcut back, nothing else.
  }
}

function forgetPendingOrder() {
  try {
    sessionStorage.removeItem(PENDING_ORDER_KEY)
  } catch {
    // As above.
  }
}

// ── The commit sheet: state ───────────────────────────────────────────────
/**
 * Declared HERE, above the payment section, because `openPaymentWait` moves the
 * sheet to its last step. The computeds and handlers that go with these live
 * further down, after the quote and the CTA label they read.
 */
type CommitStep = 'identity' | 'two-factor' | 'account' | 'review' | 'payment'

const commitOpen = ref(false)
const commitStep = ref<CommitStep>('review')
/** The server's own words when it refuses an order — shown IN the sheet. */
const orderError = ref<string | null>(null)
/** Focus goes back here on close; see `closeCommit`. */
const reviewButton = ref<any>(null)

// ── Waiting for payment ───────────────────────────────────────────────────
/**
 * The order used to end at a toast and a new browser tab: the customer paid
 * elsewhere and came back to a page that looked untouched, with no way to ask
 * whether the money had landed. This panel is that missing screen. It polls the
 * order (cheap DB read), takes an explicit "I already paid" that asks the
 * provider out of band of the 20 s reconciler tick, and is dismissible without
 * losing the order — the summary keeps a way back in.
 */
const paymentWaitOpen = ref(false)
const paymentWaitMode = ref<'order' | 'topup'>('order')
const paymentChecking = ref(false)
const paymentLastCheckedAt = ref<number | null>(null)
const paymentCheckError = ref<string | null>(null)
const paymentWaitState = ref<'waiting' | 'paid' | 'provisioning' | 'failed' | 'expired'>('waiting')
let paymentPollTimer: ReturnType<typeof setInterval> | null = null

/** Backstop cadence. The websocket is the fast path; this catches a dropped
 * socket or a tab that slept, without turning into a poll loop. */
const PAYMENT_POLL_MS = 15_000

function openPaymentWait(mode: 'order' | 'topup') {
  paymentWaitMode.value = mode
  paymentWaitState.value = 'waiting'
  paymentCheckError.value = null
  paymentLastCheckedAt.value = null
  paymentWaitOpen.value = true
  // An order's payment panel is the commit sheet's last step; a top-up's is a
  // dialog of its own. `paymentWaitOpen` remains the single flag either way,
  // because the poll is keyed on it and a poll running behind a panel nobody
  // can see is the bug this whole section exists to avoid.
  if (mode === 'order') {
    commitStep.value = 'payment'
    commitOpen.value = true
  }
  startPaymentPolling()
}

function startPaymentPolling() {
  stopPaymentPolling()
  paymentPollTimer = setInterval(() => {
    // Never force a provider call from the timer — that leg is for the button.
    void checkPaymentNow(false)
  }, PAYMENT_POLL_MS)
}
function stopPaymentPolling() {
  if (paymentPollTimer) clearInterval(paymentPollTimer)
  paymentPollTimer = null
}
onUnmounted(stopPaymentPolling)

/** Top-up has no order to read; its invoice is account-scoped, so look it up
 * among the account's invoices instead. */
async function checkTopupPayment(): Promise<boolean> {
  const externalId = pendingTopup.value?.invoiceExternalId
  if (!externalId) return false
  const invoices = await billing.fetchAccountInvoices()
  const hit = invoices.find((inv) => inv.external_id === externalId)
  return hit?.status === 'PAID'
}

async function checkPaymentNow(force: boolean) {
  if (paymentChecking.value) return
  paymentChecking.value = true
  paymentCheckError.value = null
  try {
    if (paymentWaitMode.value === 'topup') {
      if (await checkTopupPayment()) {
        paymentWaitState.value = 'paid'
        stopPaymentPolling()
        await retryAfterTopup()
      }
      return
    }

    const orderId = pendingInvoice.value?.orderId
    if (!orderId) return
    const state = await billing.fetchOrderPaymentState(orderId, force)
    if (pendingInvoice.value && state.expires_at) {
      pendingInvoice.value.expiresAt = state.expires_at
    }
    if (state.provisioned) {
      paymentWaitState.value = 'paid'
      stopPaymentPolling()
      finishPaidOrder(orderId, pendingInvoice.value?.totalCents ?? '0')
      return
    }
    if (state.failed) {
      paymentWaitState.value = 'failed'
      stopPaymentPolling()
      return
    }
    if (state.status === 'cancelled') {
      paymentWaitState.value = 'expired'
      stopPaymentPolling()
      return
    }
    if (state.paid) {
      // Money is in; the resources are still being built.
      paymentWaitState.value = 'provisioning'
      return
    }
    paymentWaitState.value = 'waiting'
  } catch (err: any) {
    paymentCheckError.value = err?.response?.data?.message ?? err.message
  } finally {
    paymentChecking.value = false
    paymentLastCheckedAt.value = Date.now()
  }
}

/** Fresh SSO link every time — the pay URL minted at order time expires. */
async function openInvoicePage() {
  // One token per click. `ssoToWhmcs` mints a SINGLE-USE code, so a second
  // press while the first is in flight spends a second one and races two tabs
  // for the same invoice. The button is disabled while loading; this is the
  // guard for the keyboard and for anything that calls this directly.
  if (openingInvoice.value) return
  const externalId =
    paymentWaitMode.value === 'topup'
      ? pendingTopup.value?.invoiceExternalId
      : pendingInvoice.value?.invoiceExternalId
  if (externalId) {
    await ssoToWhmcs(`viewinvoice.php?id=${externalId}`, {
      openInNewTab: true,
    })
    return
  }
  const fallback =
    paymentWaitMode.value === 'topup' ? pendingTopup.value?.payUrl : pendingInvoice.value?.payUrl
  if (fallback) openUrlInNewTab(fallback)
}

function reopenPaymentWait() {
  if (!pendingInvoice.value && !pendingTopup.value) return
  paymentWaitOpen.value = true
  // Same branch as `openPaymentWait`. Without it this button set a flag that
  // nothing rendered any more, and "Track payment" did nothing at all.
  if (paymentWaitMode.value === 'order') {
    commitStep.value = 'payment'
    commitOpen.value = true
  }
  startPaymentPolling()
  void checkPaymentNow(false)
}

// Closing the panel must not leave a poll running behind a hidden dialog.
watch(paymentWaitOpen, (open) => {
  if (!open) stopPaymentPolling()
})

/** What we'll actually charge: the shortfall, but never below the global minimum. */
const topupChargeCents = computed(() => {
  if (!requiresTopupState.value) return 0
  const shortfall = Number(BigInt(requiresTopupState.value.shortfallCents))
  const mins = billing.catalog?.recharge_minimums
  const ct = selectedSubscription.value?.commitment_type
  const min = mins ? Number(ct === 'COMMITTED' ? mins.committed_cents : mins.on_demand_cents) : 0
  return Math.max(shortfall, min)
})

async function generateTopupInvoice() {
  if (!requiresTopupState.value) return
  topupGenerating.value = true
  try {
    const result = await billing.topupSubscription(
      requiresTopupState.value.subscriptionId,
      topupChargeCents.value,
    )
    // Same waiting panel as a new-subscription invoice: a recharge left the
    // customer with the same "did it go through?" question and no answer.
    pendingTopup.value = {
      invoiceExternalId: result.invoice_external_id,
      payUrl: result.pay_url ?? null,
      totalCents: String(topupChargeCents.value),
    }
    // Not opening `result.pay_url` here, for the reason the order path gives:
    // after an `await` the browser blocks it. The panel's "Pay now" opens it.
    openPaymentWait('topup')
  } catch (err: any) {
    toast.add({
      title: t('billing.page.checkout.toast.topupError'),
      description: err?.response?.data?.message ?? err.message,
      color: 'error',
    })
  } finally {
    topupGenerating.value = false
  }
}

async function retryAfterTopup() {
  requiresTopupState.value = null
  pendingTopup.value = null
  paymentWaitOpen.value = false
  await fetchCheckoutSubscriptions() // refresh balances
  await placeOrder()
}

/**
 * Payment confirmed: report it, let the success state land, then move on.
 * Shared by the websocket push and the polled/forced check so the two paths
 * can never celebrate differently.
 */
function finishPaidOrder(orderId: string | null, amountCents: string) {
  // Revenue-accuracy event only — NOT the conversion (Ads imports the
  // order-placed purchase). Distinct event name so it never double-counts.
  if (orderId) {
    analytics.trackPaymentConfirmed({
      transaction_id: orderId,
      value: centsToMajor(amountCents),
    })
  }
  paymentWaitState.value = 'paid'
  stopPaymentPolling()
  toast.add({
    title: t('billing.page.checkout.toast.paymentConfirmed'),
    description: t('billing.page.checkout.toast.paymentConfirmedDesc'),
    color: 'success',
  })
  // Hold the confirmed state on screen briefly — a modal that vanishes the
  // instant it succeeds reads as a glitch, not as a confirmation.
  setTimeout(() => router.push('/billing'), 1200)
}

// ── WebSocket: react to payment confirmation in real-time ─────────────────
type BillingInvoicePaidPayload = {
  externalId: string
  orderId: string | null
  subscriptionId: string | null
  amountCents: string
  paidAt: string
}

function handleInvoicePaid(data: BillingInvoicePaidPayload) {
  // Pending new-subscription invoice confirmed
  if (pendingInvoice.value && data.externalId === pendingInvoice.value.invoiceExternalId) {
    finishPaidOrder(data.orderId, data.amountCents)
    return
  }
  // Topup invoice confirmed — retry the order automatically
  if (requiresTopupState.value && data.subscriptionId === requiresTopupState.value.subscriptionId) {
    paymentWaitState.value = 'paid'
    stopPaymentPolling()
    toast.add({
      title: t('billing.page.checkout.toast.rechargeConfirmed'),
      description: t('billing.page.checkout.toast.rechargeConfirmedDesc'),
      color: 'info',
    })
    retryAfterTopup()
  }
}

/**
 * The live "your invoice was paid" push, which only exists for a session.
 *
 * Subscribed on the session rather than on mount: an anonymous visitor has no
 * socket to listen on, and connecting anyway cost a 401 on `/socket-token` on
 * every public page load. Attaching when the session appears means somebody who
 * signs up here still gets the push for the invoice they are about to pay.
 */
watch(
  signedIn,
  (is) => {
    if (is) {
      // The mount-time attempt declined for want of a session; there is one now.
      void connectSocket()
      addListener('billingInvoicePaid', handleInvoicePaid)
    } else {
      removeListener('billingInvoicePaid', handleInvoicePaid)
    }
  },
  { immediate: true },
)
onUnmounted(() => removeListener('billingInvoicePaid', handleInvoicePaid))

const termDisplayName = computed(
  () =>
    labels.termLabel[
      billing.catalog?.terms.find((tmpl) => tmpl.kind === checkoutTerm.value)?.kind ??
        selectedSubscription.value?.term?.kind ??
        ''
    ] ?? '',
)
const selectedDatacenterName = computed(() => locationLabel(selectedDatacenter.value))

/**
 * Which subscription this is being added to, as a NAME.
 *
 * Resolved here rather than in the summary component, so the sidebar and the
 * review step are handed the same string. The bare id is the last resort and
 * deliberately still shown: an account whose subscription list has not loaded
 * is better off seeing an unfamiliar id than a blank where the answer goes.
 */
const subscriptionLabel = computed(() => {
  if (selectedSubscriptionId.value === 'new') {
    return t('billing.page.checkout.summary.newSubscription')
  }
  return selectedSubscription.value?.name ?? `${selectedSubscriptionId.value.slice(0, 16)}…`
})

function couponStatusText(preview: CouponPreview | null) {
  if (!preview) return ''
  if (preview.valid) return t('billing.page.checkout.couponStatus.applied')
  const reasons: Record<string, string> = {
    coupon_not_found: t('billing.page.checkout.couponStatus.coupon_not_found'),
    inactive: t('billing.page.checkout.couponStatus.inactive'),
    not_started: t('billing.page.checkout.couponStatus.not_started'),
    expired: t('billing.page.checkout.couponStatus.expired'),
    below_minimum_service_total: t(
      'billing.page.checkout.couponStatus.below_minimum_service_total',
    ),
    service_limit_reached: t('billing.page.checkout.couponStatus.service_limit_reached'),
    zero_discount: t('billing.page.checkout.couponStatus.zero_discount'),
  }
  return reasons[preview.reason ?? ''] ?? t('billing.page.checkout.couponStatus.not_applicable')
}

/**
 * Where the first unmet requirement lives.
 *
 * The CTA stays focusable while blocked (`aria-disabled`, not `disabled`, so a
 * keyboard user can reach it and hear why) — but pressing it then did nothing
 * at all, which reads as a broken button. It now takes the customer to the
 * thing that is missing.
 */
const BLOCKER_TARGET: Record<string, string> = {
  signIn: 'checkout-identity',
  noCapacity: 'checkout-location',
  datacenter: 'checkout-location',
  initialRecharge: 'checkout-cycle',
  plan: 'checkout-plan',
  osMin: 'checkout-plan',
  zeroRate: 'checkout-plan',
  os: 'checkout-os',
  password: 'checkout-password',
  passwordMatch: 'checkout-password',
  vpc: 'checkout-connectivity',
  subscription: 'checkout-billing',
  // The CTA, not the sidebar: the coupon field sits directly above it, and the
  // sidebar's own top is level with step one on a wide screen — so "go to the
  // summary" is "go to the top of the page".
  coupon: 'checkout-cta',
  // The licence add-ons render in the OS step (`ADDON_STEP_SLOT`), so that is
  // where "add the Windows licence, or drop the CALs" has to land. It fell
  // through to the default and sent people to the plan table.
  calWithoutWindowsLicence: 'checkout-os',
}

/**
 * The step tracker, declared HERE rather than beside the rest of its section,
 * because the scroll helpers below call `goTo` and a `const` cannot be read
 * before its initialiser has run.
 */
const progress = useCheckoutProgress()

/**
 * Bringing a step into view is `progress.goTo`, and only that.
 *
 * This file used to carry its own `scrollIntoView({ block: 'start' })`, and it
 * got all three of the things that matter wrong. It scrolled the WINDOW, which
 * is right on `/configure` and wrong on `/billing/checkout`, where the panel
 * body is the scroller. It ignored the sticky header `/configure` lays over the
 * page, so a card it "scrolled to" landed underneath it. And it moved pixels
 * without moving focus, leaving a keyboard user's caret wherever it had been.
 * `goTo` answers all three from the DOM it is handed, and honours
 * `prefers-reduced-motion` — so the local copies are deleted rather than fixed,
 * along with the local `prefersReducedMotion` they were the only readers of.
 */
function scrollToFirstBlocker() {
  const first = pageOrderBlockers.value[0]
  progress.goTo(first ? (BLOCKER_TARGET[first.key] ?? 'checkout-plan') : 'checkout-plan')
}

// ── Signing in, or signing up, without leaving the page ───────────────────
/**
 * The identity step replaces what used to be a redirect.
 *
 * The public route asked for the customer's details and then sent them to
 * `/billing/checkout` to finish, which needed a whole draft-persistence layer
 * to carry the configuration across a navigation — and threw the customer out
 * of the page at the exact moment they had committed. They now stay put: the
 * session appears underneath them and the order is placed from here.
 *
 * Flows still LEAVE this page, though: a social sign-in, because the provider
 * owns that round trip; two-factor and account selection, which hand off to
 * `/login`; and every ordinary reason a customer reloads mid-purchase. The
 * draft is what carries the configuration through all of them, and each of
 * them returns here.
 */
const draft = useCheckoutDraft(trialCode.value)

/** Announced, never silent — the same rule the automatic disk raise follows. */
const restoredFromProvider = ref(false)

/** What this account is known to hold, for the draft's second pass. */
function accountScope() {
  return {
    vpcIds: vpcs.value.map((v: any) => v.id),
    imageIds: clientImageOptions.value.map((i) => i.id),
    subscriptionIds: availableSubscriptions.value.map((sub) => sub.id),
  }
}

/**
 * The half of a restore only the signed-in account can confirm.
 *
 * Runs from every point that data can land — the initial load, a sign-in that
 * happens underneath the page, a manual VPC refresh — rather than once at
 * mount. It used to run once, against lists that on a sign-in were still empty,
 * which is what produced the half-restored page: private networking came back
 * with no network in it, and a customer's own image came back as no OS at all.
 */
function applyAccountScopedDraft() {
  const recovered = draft.restoreAccountScoped(configurator, accountScope())
  if (!recovered.applied) return
  if (recovered.couponCode && !couponCode.value) couponCode.value = recovered.couponCode
  if (recovered.targetSubscriptionId && selectedSubscriptionId.value === 'new') {
    selectedSubscriptionId.value = recovered.targetSubscriptionId
  }
  restoredFromProvider.value = true
}

/**
 * What the commercial website's marketplace asked for — `?template=<code>`,
 * optionally with `&plan=<offering_code>`.
 *
 * The marketplace pages on masterdaweb.com are the top of this funnel: somebody
 * reads about WordPress, or about Ubuntu 24.04 in particular, and clicks
 * "deploy". Landing them on an empty configurator throws that decision away and
 * asks them to find it again among sixty-odd images — which is the exact step
 * the marketplace exists to remove.
 *
 * Both codes are checked against the catalog rather than trusted. A link
 * outlives what it names: bookmarks, search results and old posts all keep
 * pointing at templates that were since withdrawn, and a code we cannot resolve
 * has to leave the customer on a working checkout rather than on one insisting
 * it selected something they cannot see.
 */
const preselectedFromSite = ref<{
  templateName: string | null
  planName: string | null
  planTooSmall: boolean
} | null>(null)

function applyUrlPreselection() {
  const templateCode = typeof route.query.template === 'string' ? route.query.template : null
  const planCode = typeof route.query.plan === 'string' ? route.query.plan : null
  const termCode = typeof route.query.term === 'string' ? route.query.term : null
  if (!templateCode && !planCode && !termCode) return

  // Without a catalog nothing can be validated, so nothing is applied — and the
  // parameters are LEFT in the URL, because the next load is the one chance to
  // honour them. Every other path below consumes them.
  const catalog = billing.catalog
  if (!catalog) return

  const template = templateCode
    ? (catalog.templates.find((tmpl) => tmpl.code === templateCode) ?? null)
    : null

  if (template) {
    // The tab goes first: `selectedTemplateCode`'s setter writes to whichever
    // tab is active, so setting the code while the wrong one is open files it
    // where nothing reads it.
    osTab.value = template.template_type === 'MARKETPLACE_APP' ? 'marketplace' : 'os'
    selectedTemplateCode.value = template.code
  }

  let offering = planCode ? (computeOfferings.value.find((o) => o.code === planCode) ?? null) : null
  // A plan the chosen OS cannot run on is dropped rather than selected. Keeping
  // it would open the page on a configuration it refuses to quote, with the
  // reason rendered two steps below where they are looking.
  const planTooSmall = !!offering && !!template && !offeringMeetsOsMin(offering)
  if (planTooSmall) offering = null
  if (offering) selectedOfferingCode.value = offering.code

  // A billing cycle, when the page that linked here was selling one — the
  // marketplace's "or R$ x/month on the annual plan" line. Validated against
  // the catalog's terms like everything else; an unknown value is ignored
  // rather than guessed, and it only ever applies to a NEW subscription (an
  // existing one prices at its own cycle, which `termOverride` enforces).
  const term = termCode
    ? (billing.catalog?.terms.find((t) => t.kind === termCode)?.kind ?? null)
    : null
  if (term) selectedTerm.value = term

  if (template || offering || planTooSmall) {
    preselectedFromSite.value = {
      templateName: template?.name ?? null,
      planName: offering?.name ?? null,
      planTooSmall,
    }
  }

  // Drop only what we consumed. `sub`, the attribution parameters and whatever
  // else a campaign appended belong to other readers and stay where they are.
  const { template: _template, plan: _plan, term: _term, ...rest } = route.query
  router.replace({ path: route.path, query: rest })
}

onMounted(() => {
  if (
    draft.restore(configurator, billing.catalog, {
      datacenterIds: datacenters.value.map((d: any) => d.id),
    })
  ) {
    restoredFromProvider.value = true
  }
  const savedRecharge = draft.pending.value?.initialCreditAmount
  if (savedRecharge && initialCreditCents(savedRecharge)) {
    initialRechargeAmount.value = savedRecharge
  }
  applyAccountScopedDraft()
  // Last, so a link the customer just clicked outranks a draft left in the tab
  // — for the OS and the plan, and nothing else. Location, storage, add-ons and
  // the coupon stay exactly as the draft restored them.
  applyUrlPreselection()
  // Keep the trial on its URL: reloads and sign-in return to this offer;
  // an ordinary checkout link must never inherit it from session storage.
  void resolveTrial()
})

// The lists that decide the second pass all arrive asynchronously, and on a
// sign-in they arrive AFTER the page mounted. Watch them rather than guess when
// they are ready; `restoreAccountScoped` is idempotent, so a list that settles
// twice costs nothing.
watch(
  [vpcs, clientImageOptions, availableSubscriptions],
  () => {
    if (draft.pending.value) applyAccountScopedDraft()
  },
  { deep: true },
)

/**
 * The one thing a restore cannot bring back.
 *
 * The access password is deliberately never written to storage — it is the
 * credential for the machine the customer is buying, and the one thing here
 * that grants access to anything. So it comes back empty, and saying nothing
 * would leave them staring at a field they had already filled in wondering what
 * else was lost.
 */
const passwordNeedsReentry = computed(
  () => restoredFromProvider.value && requiresPassword.value && !password.value,
)

/**
 * Persisted for the whole purchase, session or no session.
 *
 * This was gated on `signedIn`, on the reasoning that a draft only had to
 * survive the anonymous half of the flow. It does not: a customer who signs in
 * and then opens their image library, adds a VPC or tops up their credit comes
 * back through a page load like any other, and a signed-in customer who simply
 * refreshed came back to a checkout too empty to price anything at all.
 */
watch(
  () =>
    draft.snapshot(configurator, {
      clientImageId: selectedClientImageId.value,
      trialCode: trialCode.value,
      // The access password is the one thing that is never written down.
      couponCode: couponCode.value,
      targetSubscriptionId: selectedSubscriptionId.value,
      initialCreditAmount: String(initialRechargeAmount.value),
    }),
  (value) => {
    draft.save(value)
    // The configuration moved, so the intent did: the key covers the body, and
    // reusing it for a different order is refused rather than replayed.
    orderIntentKey.value = null
  },
  { deep: true },
)

/**
 * A session appeared. Runs ONCE, whatever brought it.
 *
 * Two things call this, and both fire on every registration: the identity
 * step's `complete` emit, and the `signedIn` watcher below — because
 * `applySessionData` flips `signedIn` while the emit's handler is still parked
 * on the `await` a line down. Ungated that meant two `sign_up` events with
 * different `method`, two rounds of these three refetches, and two smooth
 * scrolls racing each other, which is what read to customers as the page
 * reloading itself.
 *
 * The emit wins the race (it is synchronous; the watcher is queued `pre`), so
 * the specific method is still the one recorded. The guard is nonetheless
 * method-agnostic: firing once matters more than labelling it right.
 */
let identityHandled = false

async function onIdentityComplete(method: 'password' | 'social' | 'existing-account' = 'password') {
  if (identityHandled) return
  identityHandled = true

  analytics.trackSignUp(method)
  await Promise.all([fetchVpcs(), fetchCheckoutSubscriptions(), refreshClientImages()])
  // Only NOW can the account-scoped half of the draft be checked against
  // anything. Clearing the draft here — which is what used to happen, before
  // these requests had even been made — is what threw away the VPC, the image
  // and the coupon of every customer who signed in mid-purchase.
  applyAccountScopedDraft()
  await nextTick()

  // A CONFIGURATION problem is the only thing that sends them back up the
  // page: it is a decision only the flow can take, and there is nothing to
  // confirm until it is made.
  //
  // Everything else that can still be outstanding at this moment is asked for
  // where they already are. This used to scroll to `#checkout-summary`, which
  // is the STICKY SIDEBAR — its top is level with step one for the whole page,
  // so "scroll to the summary" was literally "scroll to the top" on a desktop,
  // and "scroll past everything" on a phone, where that column stacks last.
  const configBlocker = configurationBlockers.value[0]
  if (configBlocker) {
    commitOpen.value = false
    progress.goTo(BLOCKER_TARGET[configBlocker.key] ?? 'checkout-plan')
    return
  }
  // Straight to the thing they came to do. The access password is asked for
  // there rather than back up the page, which matters because it is the one
  // choice the draft never keeps and is therefore empty on exactly this path.
  openCommitAt('review')
}

// Signed in in another tab, or arrived here already signed in. Kept alongside
// the emit rather than replaced by it: this is the only thing that catches a
// session the identity step did not mint.
watch(signedIn, (is, was) => {
  if (is && !was) void onIdentityComplete('existing-account')
})

/** Jump to the storage step — the answer to "can I make the disk bigger?"
 * has to be one click away from where the question is asked. */
function scrollToStorage() {
  progress.goTo('checkout-storage')
}

/** Same, for the OS picker's "this version needs a bigger plan". RAM and vCPU
 * are bought with the plan, and the plan cards already disable the ones this
 * OS cannot run on — so the fix is to land the customer back on that list. */
/**
 * A plan the chosen image cannot run: its cells are dimmed, never the row, and
 * never disabled — a customer may pick the plan first and the image after, and
 * a row that refused the click would read as broken. The reason stays at full
 * strength, because it is the one thing on the row worth reading then.
 */
function planDimmed(o: CatalogOffering): boolean {
  return !!selectedTemplate.value && selectedOfferingCode.value !== o.code && !offeringMeetsOsMin(o)
}

/**
 * A row too small for the chosen image is quieted with the MUTED colour,
 * never with opacity: opacity took its text to about 2:1 on white, and the
 * row is still a choice somebody reads and may make.
 */
function planMainTone(o: CatalogOffering): string {
  return planDimmed(o) ? 'text-muted' : ''
}

/** What a plan lacks to run the chosen image ("4 GB RAM"), for its row. */
function planShortfallLabel(o: CatalogOffering): string {
  return specLabel({
    vcpu: osMinVcpu.value != null && o.vcpu < osMinVcpu.value ? osMinVcpu.value : null,
    ramMb: osMinRamMb.value != null && o.ram_mb < osMinRamMb.value ? osMinRamMb.value : null,
  })
}

function scrollToPlan() {
  progress.goTo('checkout-plan')
}

// ── Section completion (numbered step state in the card headers) ─────────
// Location, plan, OS, storage and connectivity come from the configurator; only
// the password is this page's own, because only this page asks for one.
const passwordDone = computed(
  () => !requiresPassword.value || (isPasswordStrong.value && passwordsMatch.value),
)
const hasSubscriptionChoice = computed(
  () =>
    !trialActive.value && !loadingSubscriptions.value && availableSubscriptions.value.length > 0,
)

// ── Add-ons, placed beside the decision each one modifies ─────────────────
/**
 * The visible add-ons belonging to one step.
 *
 * Anything the mapping does not know about lands in `extras`, which is what
 * keeps a newly-seeded `addon_type` sellable the day it appears instead of
 * silently vanishing from the checkout. See `~/utils/checkout-addons`.
 */
function addonsForStep(slot: CheckoutAddonSlot) {
  return addonsInSlot(
    (billing.catalog?.addons ?? []).filter((a) => trialAllows(a.addon_type)),
    slot,
    isAddonVisible,
  )
}

/**
 * Add-ons with no step of their own. Only these still need a card, so the
 * standalone "Optional" step now exists for the leftovers rather than for
 * everything.
 */
const looseAddons = computed(() => addonsForStep('extras'))

/**
 * Step numbers, renumbered around the steps that are not always there — the
 * password step only exists for images that ask for one. A static sequence
 * would skip a number and read as a step the customer had missed.
 *
 * The image is 2, before the cycle (3), the plan (4) and storage (5): it
 * decides what the plan must hold and the disk floor, so asking it first is
 * what lets the plan table say which plans can run it, and what stops the disk
 * being rewritten behind a customer who has already sized it.
 */
const stepNumbers = computed(() => {
  // Start with the first decision that changes the server. Billing is last and
  // only exists when there is actually an existing subscription to choose.
  let n = 5
  const password = requiresPassword.value ? ++n : null
  const connectivity = ++n
  const addons = looseAddons.value.length > 0 ? ++n : null
  const subscription = hasSubscriptionChoice.value ? ++n : null
  // Only while there is nobody to bill. It disappears the moment a session
  // exists, which is also the moment its number stops being needed.
  const identity = signedIn.value ? null : ++n
  return {
    location: 1,
    os: 2,
    cycle: 3,
    plan: 4,
    storage: 5,
    password,
    connectivity,
    addons,
    subscription,
    identity,
  }
})

// ── Progress tracker (rendered by the shell, in the page header) ──────────
/**
 * What the header's step tracker shows.
 *
 * Built from the SAME refs the step cards are, and in the same order, because
 * the tracker's whole value is that it can be trusted from a distance: a chip
 * that says "done" over a card that says otherwise is worse than no chip. Every
 * field here is either the card's own `v-if` (`reachable`) or the card's own
 * `done` / `attention` / `optional` prop — nothing is recomputed.
 *
 * `reachable` is what separates "you skipped this" from "we have not asked
 * yet": a step whose card is not rendered cannot be jumped to and must never
 * be counted against the customer.
 *
 * Declared further up, beside the scroll helpers — see the note there.
 */

/**
 * Which blockers belong to which step.
 *
 * `pageOrderBlockers` already IS the answer to "what still needs doing" — it is
 * what the CTA refuses on and what the sidebar checklist prints — and
 * `BLOCKER_TARGET` already says where each one lives. Deriving the tracker
 * from the pair means the bar, the button and the checklist cannot come to
 * different conclusions, and a new blocker joins the tracker for free.
 */
const blockersByStep = computed(() => {
  const map = new Map<string, { key: string; label: string }[]>()
  for (const blocker of pageOrderBlockers.value) {
    const anchor = BLOCKER_TARGET[blocker.key] ?? 'checkout-plan'
    const list = map.get(anchor)
    if (list) list.push(blocker)
    else map.set(anchor, [blocker])
  }
  return map
})

/**
 * Blockers that mean "this is WRONG", as against "you have not got to it yet".
 *
 * Only these earn the amber marker. An unfilled step is the normal state of a
 * form you are halfway down; warning about it would make the warning colour
 * mean nothing by the third step.
 */
const WRONG_NOT_UNFILLED = new Set([
  'noCapacity',
  'osMin',
  'zeroRate',
  'calWithoutWindowsLicence',
  'initialRecharge',
  'passwordMatch',
  'coupon',
])

const selectedLooseAddonCount = computed(
  () => looseAddons.value.filter((a) => (addonQuantities.value[a.code] ?? 0) > 0).length,
)

const candidateSteps = computed<CheckoutProgressStep[]>(() => {
  // Nothing to guide anyone through until the catalog is there — the flow
  // renders a loading line, and a tracker over it would be describing a page
  // that does not exist yet.
  if (loadingCatalog.value || !billing.catalog || loadingDcs.value) return []

  const n = stepNumbers.value
  const hasLocation = !!selectedDatacenterId.value
  const hasPlan = !!selectedOffering.value

  /**
   * One step, classified against the blockers it currently owns.
   *
   * `baseline` is what the step asks for when nothing is wrong. A `default`
   * step that acquires a blocker is promoted to `required` — that is how
   * "which VPC?" becomes a real question the moment private networking is
   * chosen — and a `required` step with no blocker left is, by definition,
   * answered.
   */
  const step = (
    key: string,
    number: number | null,
    label: string,
    anchor: string,
    baseline: CheckoutStepRequirement,
    reachable: boolean,
    value: string | null = null,
  ): CheckoutProgressStep => {
    const blocking = blockersByStep.value.get(anchor) ?? []
    return {
      key,
      number,
      label,
      anchor,
      requirement: baseline,
      blocked: reachable && blocking.length > 0,
      /*
       * Settled: nothing here is waiting on the customer.
       *
       * A preselected step counts. "Belo Horizonte" and "Mensal" ARE answers —
       * the flow chose them so the customer would not have to — and a step
       * holding a real value while showing a hollow number reads as one they
       * still owe. Withholding the tick from those was over-correcting: what
       * the earlier version got wrong was not the ticks, it was that nothing
       * on the rail then pointed at the one step that DID want an answer.
       * Pointing is the outstanding ring and the count beside it, and neither
       * needs the settled steps to look unfinished.
       *
       * The exception is a step with nothing in it and nothing required — the
       * extras nobody added. There is no answer there to report, so it stays
       * "Optional" rather than claiming a decision that was never made.
       */
      done: reachable && blocking.length === 0 && (baseline === 'required' || value != null),
      attention: blocking.some((b) => WRONG_NOT_UNFILLED.has(b.key)),
      reachable,
      // Only once the step is on the page: naming the value of a step the
      // customer has not been offered yet reads as a decision made for them.
      value: reachable ? value || null : null,
    }
  }

  const steps: CheckoutProgressStep[] = [
    // With one deployable location there is nothing to pick, and saying
    // "completed" about the only option the customer was ever offered is
    // exactly the empty progress this bar is supposed to avoid.
    step(
      'location',
      n.location,
      t('billing.page.checkout.steps.location'),
      'checkout-location',
      deployableDatacenters.value.length > 1 ? 'required' : 'default',
      true,
      selectedDatacenterName.value,
    ),
    // The image right after the location, as on the page: it decides what the
    // plan has to hold, so it is asked before the plan is.
    step(
      'os',
      n.os,
      t('billing.page.checkout.steps.os'),
      'checkout-os',
      'required',
      hasLocation,
      osDisplayName.value,
    ),
    // Every cycle is a valid answer and Monthly is pre-selected; it is here to
    // be reconsidered for the discount, not to be filled in.
    step(
      'cycle',
      n.cycle,
      t('billing.page.checkout.steps.cycle'),
      'checkout-cycle',
      'default',
      hasLocation,
      termDisplayName.value,
    ),
    step(
      'plan',
      n.plan,
      t('billing.page.checkout.steps.plan'),
      'checkout-plan',
      'required',
      hasLocation,
      selectedOffering.value?.name ?? null,
    ),
    // The disk arrives sized, and the OS minimum raises it automatically.
    step(
      'storage',
      n.storage,
      t('billing.page.checkout.steps.disks'),
      'checkout-storage',
      'default',
      hasPlan,
      `${totalStorageGb.value} GB`,
    ),
  ]

  if (n.password != null) {
    steps.push(
      step(
        'password',
        n.password,
        t('billing.page.checkout.steps.password'),
        'checkout-password',
        'required',
        hasPlan && requiresPassword.value,
      ),
    )
  }

  // A public address is the default and needs no answer; choosing private
  // networking is what turns this into a question, and the `vpc` blocker is
  // what says so.
  steps.push(
    step(
      'connectivity',
      n.connectivity,
      t('billing.page.checkout.steps.connectivity'),
      'checkout-connectivity',
      'default',
      hasPlan,
      connectivityMode.value === 'private'
        ? (selectedVpcName.value ?? t('billing.page.checkout.connectivity.private'))
        : t('billing.page.checkout.connectivity.public'),
    ),
  )

  if (n.addons != null) {
    steps.push(
      step(
        'addons',
        n.addons,
        t('billing.page.checkout.steps.addons'),
        'checkout-addons',
        'optional',
        hasPlan && looseAddons.value.length > 0,
        selectedLooseAddonCount.value > 0
          ? t(
              'billing.page.checkout.progress.addonsChosen',
              { count: selectedLooseAddonCount.value },
              selectedLooseAddonCount.value,
            )
          : null,
      ),
    )
  }

  // "New invoice" is a working default; this only becomes a question once the
  // customer arms "use an existing subscription" without naming one.
  if (n.subscription != null) {
    steps.push(
      step(
        'subscription',
        n.subscription,
        t('billing.page.checkout.steps.billing'),
        'checkout-billing',
        'default',
        hasPlan && hasSubscriptionChoice.value,
        selectedSubscriptionId.value === 'new'
          ? t('billing.page.checkout.summary.newSubscription')
          : (selectedSubscription.value?.name ?? null),
      ),
    )
  }

  if (n.identity != null) {
    steps.push(
      step(
        'identity',
        n.identity,
        t('billing.page.checkout.identity.title'),
        'checkout-identity',
        'required',
        hasPlan && !signedIn.value,
      ),
    )
  } else if (hasPlan) {
    /*
     * Answered — and it keeps its tick.
     *
     * `stepNumbers` drops the identity step the instant a session exists, so
     * `candidateSteps` stopped emitting it at all, and `progressSteps` can only
     * keep what it is handed. The chip therefore VANISHED mid-purchase and every
     * chip after it shifted along, which is layout movement at the exact moment
     * this flow is trying to hold still — and it falsified the one invariant the
     * a11y suite asserts about the rail.
     *
     * Written out rather than passed through `step()`, because it is no longer
     * `reachable` — there is no card left to jump to — and that helper reads
     * unreachable as unanswered. Which is right for a step the flow has not
     * offered yet, and wrong for one the customer has just finished. The filter
     * in `progressSteps` is what keeps this honest: it only survives if the
     * customer was actually asked, so somebody arriving already signed in
     * (every visit to `/billing/checkout`) never sees a step they never saw.
     */
    steps.push({
      key: 'identity',
      number: null,
      label: t('billing.page.checkout.identity.title'),
      anchor: 'checkout-identity',
      requirement: 'required',
      blocked: false,
      done: true,
      attention: false,
      reachable: false,
    })
  }

  // The destination. It has no card — it is the summary — so it carries a flag
  // rather than a number, and it reports on the ORDER rather than on a section
  // of the form. Without it the tracker stops one step short of the thing the
  // customer came to do.
  steps.push({
    key: 'review',
    number: null,
    label: t('billing.page.checkout.progress.review'),
    anchor: 'checkout-summary',
    requirement: 'required',
    blocked: !canPlaceOrder.value,
    done: canPlaceOrder.value,
    attention: false,
    reachable: true,
    final: true,
  })

  return steps
})

/**
 * The tracker lists ONLY what the customer has to answer.
 *
 * Most of this flow arrives already decided — the disk sizes itself from the
 * plan, a single deployable location is preselected, the cycle defaults to
 * Monthly — and none of that is a task. Listing it made the bar a table of
 * contents for a form rather than a list of what is left, which is the one
 * thing a customer halfway down a long checkout cannot work out for
 * themselves. A step earns its place by ASKING for something.
 *
 * Whether it is asking is not fixed per step: a single-datacenter catalog has
 * nothing to choose, a multi-datacenter one does, and choosing private
 * networking turns "which VPC?" into a real question. `requirement` already
 * decides that from the live blockers, so this is a filter over it rather
 * than a second opinion.
 */
const askedStepKeys = ref(new Set<string>())

watch(
  candidateSteps,
  (list) => {
    for (const step of list) {
      if (step.reachable && step.requirement === 'required') {
        askedStepKeys.value.add(step.key)
      }
    }
  },
  { immediate: true, deep: true },
)

/**
 * Once a step has asked, it STAYS — as a tick.
 *
 * Dropping it the moment it was answered would delete the evidence of
 * progress: every step would be either outstanding or gone, the bar would read
 * zero for the whole purchase, and the customer would watch the thing they
 * just did vanish. The list only ever grows, which is also what keeps the
 * percentage from walking backwards as steps appear.
 */
const progressSteps = computed<CheckoutProgressStep[]>(() => {
  const shown = candidateSteps.value.filter(
    (step) => askedStepKeys.value.has(step.key) || step.blocked,
  )
  /*
   * Numbered over the LIST, not over the cards.
   *
   * The chips used to carry each step's card number, which was contiguous only
   * while the tracker listed every card. Filtered down to the steps that ask
   * for something it read 1, 4, 6 — and a to-do list with holes in it looks
   * like one that has lost items, which is the opposite of what a tracker is
   * for. The closing step keeps its flag: it is the destination rather than
   * another thing to fill in, and numbering it would invite someone to look
   * for a card that does not exist.
   */
  let n = 0
  return shown.map((step) => ({ ...step, number: step.final ? null : ++n }))
})

watch(progressSteps, (value) => progress.publish(value), { immediate: true })
// The tracker lives in the shell's header, which outlives this component — so
// both the steps it renders and the way into the closing one are handed over,
// and both are given back on unmount. The handler is a module slot in that
// composable (a function cannot live in `useState`), so failing to clear it
// would leave a closure over a dead component behind.
// The page's rail describes the configurator; the sheet's describes the
// purchase. Only one of them is useful at a time, and the sheet is the one on
// top — so the shell's stands down while it is open.
watch(commitOpen, (open) => progress.setSuppressed(open))

onUnmounted(() => progress.reset())

// ── Coming back from a social provider ────────────────────────────────────
/**
 * The provider's outcome, whichever way it reached us.
 *
 * Registered HERE rather than in the identity card, because that card unmounts
 * the instant a session exists — and `useSocialLogin`'s own docblock already
 * warns that the component which opens a popup can unmount while it is still
 * open. Cleared on unmount, or a popup started later from `/login` would be
 * handed to a checkout that is no longer on screen.
 */
const socialParams = ref<Record<string, string> | null>(null)

onMounted(() => {
  // Desktop popup: the relay hands us the params with no navigation at all,
  // so nothing here is ever torn down.
  setSocialCallbackHandler((params) => {
    socialParams.value = params
  })
  // Same tab (a phone, or a blocked popup): the callback page stashed them and
  // sent the customer straight back here.
  const handed = useSocialHandoff().take()
  if (handed) socialParams.value = handed
})

onUnmounted(() => setSocialCallbackHandler(null))

/**
 * Put a placed-but-unpaid order back on screen after a reload.
 *
 * Asked WITHOUT `refresh=true` on purpose: that leg calls the provider and
 * carries a per-invoice cooldown, which belongs to the customer's explicit "I
 * already paid" rather than to a page load. The cheap DB read is enough to
 * decide whether there is anything to show.
 *
 * Any terminal state clears the stash, so a spent order cannot resurrect the
 * panel over the next thing they configure — and so does any error, because a
 * panel about an order we cannot describe is worse than no panel.
 */
async function restorePendingOrder() {
  // The saved invoice belongs to the ordinary cart, not this trial offer.
  if (trialActive.value) return
  if (!signedIn.value) return
  let orderId: string | null = null
  try {
    orderId = sessionStorage.getItem(PENDING_ORDER_KEY)
  } catch {
    return
  }
  if (!orderId) return

  try {
    const state = await billing.fetchOrderPaymentState(orderId, false)
    const invoice = (state as any).invoice
    const expired = state.expires_at ? new Date(state.expires_at).getTime() < Date.now() : false
    if (
      state.provisioned ||
      state.paid ||
      state.failed ||
      state.status === 'cancelled' ||
      !invoice ||
      invoice.status !== 'OPEN' ||
      expired
    ) {
      forgetPendingOrder()
      return
    }
    pendingInvoice.value = {
      orderId,
      invoiceExternalId: String(invoice.external_id),
      // Not stored, and not needed: "Pay now" prefers a WHMCS SSO hop anyway.
      payUrl: null,
      totalCents: String(invoice.amount_cents ?? '0'),
      initialCreditCents: state.initial_credit_cents ?? null,
      grossCents: null,
      discountCents: null,
      // Same reason as payUrl: the panel reports what is owed, not how the
      // charge was composed, so the breakdown is not restorable from it.
      balanceAppliedCents: null,
      minimumRechargeCreditCents: null,
      chargedHours: null,
      cycleEndsAt: null,
      termCents: null,
      // The coupon was applied when the invoice was issued; the panel only
      // reports the amount owed, so there is nothing to restore here.
      coupon: null,
      expiresAt: state.expires_at ?? null,
    }
    openPaymentWait('order')
  } catch {
    forgetPendingOrder()
  }
}

onMounted(() => void restorePendingOrder())

// The order is settled — nothing left to come back to.
watch(paymentWaitState, (value) => {
  if (value === 'paid' || value === 'failed' || value === 'expired') forgetPendingOrder()
})

/**
 * Whatever came back, it is answered in the sheet.
 *
 * Even an ERROR: the configuration is still on the page behind it, and the
 * alternative is a customer who tapped "Continue with Google" and was returned
 * to a page that looks exactly as they left it.
 */
watch(socialParams, (params) => {
  if (!params) return
  // Already signed in — in another tab, or because this handoff had already
  // been redeemed. The round trip got what it was for, so there is nothing to
  // ask; opening an identity form at somebody who is signed in reads as though
  // their sign-in had failed.
  if (signedIn.value) {
    // Nothing to ask: the round trip got what it was for. The page behind is
    // the review — the sidebar lists the whole order and carries the button.
    socialParams.value = null
    return
  }
  openCommitAt('identity')
})

// ── CTA label ─────────────────────────────────────────────────────────────
/**
 * Committed attach: what the usable balance (temporary + available, never the
 * negative bucket) leaves uncovered of the estimated prorata. Null when the
 * order is not an attach onto a COMMITTED subscription; 0n when the balance
 * covers it.
 */
const attachShortfall = computed<bigint | null>(() => {
  const sub = selectedSubscription.value
  const prorata = attachProrataEstimate.value
  if (
    selectedSubscriptionId.value === 'new' ||
    sub?.commitment_type !== 'COMMITTED' ||
    !sub.balance ||
    !prorata
  ) {
    return null
  }
  const usable =
    BigInt(sub.balance.temporary_micro_cents) + BigInt(sub.balance.available_micro_cents)
  const short = BigInt(prorata) - usable
  return short > 0n ? short : 0n
})

/**
 * COMMITTED existing sub whose usable balance won't cover the estimated
 * prorata: the server will answer with an invoice (pending_payment), so the
 * CTA must say "invoice", not "use balance". Estimate only — the server
 * decides authoritatively.
 */
const existingSubWillInvoice = computed(() => {
  const short = attachShortfall.value
  return short != null && short > 0n
})

/**
 * What the server will take off that invoice: the usable balance, capped at
 * the charge. The customer who deleted an instance and came back holding its
 * credit must see, BEFORE the click, that the credit is what pays most of the
 * new order — the server used to invoice the whole term and only spend the
 * credit at payment. Estimate only: the server also subtracts what other
 * pending orders already claim.
 */
const attachBalanceAppliedEstimate = computed<string | null>(() => {
  if (!existingSubWillInvoice.value) return null
  const sub = selectedSubscription.value
  const prorata = attachProrataEstimate.value
  if (!sub?.balance || !prorata) return null
  const usable =
    BigInt(sub.balance.temporary_micro_cents) + BigInt(sub.balance.available_micro_cents)
  if (usable <= 0n) return null
  const charge = BigInt(prorata)
  return (usable < charge ? usable : charge).toString()
})
/** The netted remainder: the prorata minus whatever balance comes off it. */
const attachInvoiceEstimate = computed<string | null>(() => {
  const prorata = attachProrataEstimate.value
  if (!existingSubWillInvoice.value || !prorata) return null
  const applied = attachBalanceAppliedEstimate.value
  return (BigInt(prorata) - BigInt(applied ?? '0')).toString()
})
/**
 * The minimum recharge the subscription is under, from the catalog — the same
 * floor the top-up form applies. Every invoice the checkout raises for an
 * existing subscription is a recharge, so it obeys it too.
 */
const attachMinimumRechargeMicroCents = computed<bigint>(() => {
  const mins = billing.catalog?.recharge_minimums
  const ct = selectedSubscription.value?.commitment_type
  if (!mins || !ct) return 0n
  try {
    return BigInt(ct === 'COMMITTED' ? mins.committed_cents : mins.on_demand_cents) * 10_000n
  } catch {
    return 0n
  }
})
/**
 * What the minimum recharge will add above the netted remainder (µ¢), or
 * null when the remainder already meets it. Said BEFORE the click: the
 * customer who holds most of the price in credit would otherwise read a
 * R$ 0,22 estimate and receive a R$ 20 invoice. The difference is theirs —
 * it stays on the subscription as credit.
 */
const attachMinimumRechargeCreditEstimate = computed<string | null>(() => {
  const remainder = attachInvoiceEstimate.value
  const min = attachMinimumRechargeMicroCents.value
  if (!remainder || min <= 0n) return null
  const r = BigInt(remainder)
  return r < min ? (min - r).toString() : null
})
/** What the invoice will be: the remainder, or the minimum when it is below it. */
const attachInvoiceTotalEstimate = computed<string | null>(() => {
  const remainder = attachInvoiceEstimate.value
  if (!remainder) return null
  const extra = attachMinimumRechargeCreditEstimate.value
  return (BigInt(remainder) + BigInt(extra ?? '0')).toString()
})
const ctaLabel = computed(() => {
  if (trialActive.value) return t('trial.review')
  if (selectedSubscriptionId.value === 'new') return t('billing.page.checkout.cta.newSub')
  if (requiresTopupState.value) return t('billing.page.checkout.cta.checkBalance')
  if (existingSubWillInvoice.value) return t('billing.page.checkout.cta.newSub')
  return t('billing.page.checkout.cta.confirm')
})
const ctaIcon = computed(() => {
  if (trialActive.value) return 'i-lucide-arrow-right'
  if (selectedSubscriptionId.value === 'new') return 'i-lucide-receipt'
  if (requiresTopupState.value) return 'i-lucide-refresh-cw'
  if (existingSubWillInvoice.value) return 'i-lucide-receipt'
  return 'i-lucide-check-circle'
})

// ── The commit sheet: what it shows, and how it is driven ─────────────────
/**
 * One surface for everything after "I want this".
 *
 * Identity, the second factor, account selection, review and waiting for
 * payment are steps of ONE overlay rather than places on the page, because
 * configuring a server and buying it are different jobs: the first is
 * exploratory, the second is linear, and sharing one scroll made the second
 * inherit the distractions of the first. On a phone it was worse than
 * distracting — the form ran to the bottom of the page, underneath a fixed
 * total bar and beside the assistant button.
 *
 * The page keeps every ref; the sheet is chrome. Nothing here is a navigation,
 * so the configuration behind it is never torn down and dismissing costs the
 * customer nothing.
 */
/** Two of the five steps are states of "who is buying", not places of their own. */
const commitIndicatorStep = computed(() =>
  commitStep.value === 'two-factor' || commitStep.value === 'account'
    ? 'identity'
    : commitStep.value,
)

/**
 * Once asked, a step STAYS — as a tick.
 *
 * Signing in flips `signedIn`, which would otherwise drop the identity chip
 * out of the indicator the instant the customer finished it, renumbering
 * everything after it. Same mistake the page's own rail used to make, and the
 * same fix: the list only grows.
 */
const commitAskedIdentity = ref(false)
watch(
  () => commitOpen.value && commitIndicatorStep.value === 'identity',
  (asking) => {
    if (asking) commitAskedIdentity.value = true
  },
  { immediate: true },
)

/**
 * The indicator only shows what this customer will actually pass through.
 * Somebody who arrives already signed in never sees an identity chip they
 * were never asked for.
 */
const commitSteps = computed(() => {
  const steps: { key: string; label: string }[] = []
  if (!signedIn.value || commitAskedIdentity.value) {
    steps.push({
      key: 'identity',
      label: t('billing.page.checkout.commit.stepIdentity'),
    })
  }
  steps.push({
    key: 'review',
    label: t('billing.page.checkout.commit.stepReview'),
  })
  if (!trialActive.value) {
    steps.push({
      key: 'payment',
      label: t('billing.page.checkout.commit.stepPayment'),
    })
  }
  return steps
})

/**
 * The collapsed summary: the price, and enough of the machine to recognise it.
 *
 * Always on screen, on every step — it is the reason the customer is filling in
 * a form, and the thing a nine-field billing address most easily makes them
 * forget.
 */
const commitSummaryLine = computed(() => {
  // Nothing to summarise once the order is placed and the draft is gone: the
  // payment step names the invoice itself.
  if (!selectedOffering.value) return null
  const parts: string[] = []
  const monthly = quote.value ? pricing.perMonth(quote.value.total_hourly_rate_micro_cents) : null
  if (trialActive.value) {
    parts.push(t('trial.zeroToday', { zero: billing.formatCents('0') }))
  } else if (orderInitialCreditCents.value) {
    parts.push(
      `${t('billing.page.checkout.recharge.title')}: ${billing.formatCents(
        orderInitialCreditCents.value,
      )}`,
    )
  } else if (monthly != null) {
    parts.push(`${pricing.fmt(monthly)}${t('billing.page.checkout.term.perMonth')}`)
  }
  if (selectedOffering.value) parts.push(selectedOffering.value.name)
  const os = selectedTemplate.value?.name ?? selectedClientImage.value?.name
  if (os) parts.push(os)
  return parts.length ? parts.join(' · ') : null
})

/** The amount on the button, because a confirm button that hides the figure is
 *  the one place a customer is entitled to be suspicious. */
const commitConfirmLabel = computed(() => {
  if (trialActive.value) return t('trial.start')
  if (orderInitialCreditCents.value) {
    return t('billing.page.checkout.commit.confirmCta', {
      amount: billing.formatCents(orderInitialCreditCents.value),
    })
  }
  const invoiced = selectedSubscriptionId.value === 'new' || existingSubWillInvoice.value
  const total = quote.value?.total_cycle_micro_cents
  if (invoiced && total) {
    return t('billing.page.checkout.commit.confirmCta', {
      amount: billing.formatMicroCents(total),
    })
  }
  return t('billing.page.checkout.commit.confirmCtaBalance')
})

/** Only the blockers this sheet cannot answer itself carry a way back. */
const commitBlockers = computed(() =>
  orderBlockers.value.map((b) => ({
    ...b,
    anchor: BLOCKER_TARGET[b.key] ?? null,
  })),
)

/** A coupon is only worth asking about again while it is what is in the way. */
const couponBlocked = computed(() => orderBlockers.value.some((b) => b.key === 'coupon'))

/**
 * The heading. One title for the whole purchase, except on the payment step,
 * where the body already carries a large status of its own and two competing
 * headings would just be two headings.
 */
const commitTitle = computed(() =>
  commitStep.value === 'payment'
    ? t('billing.page.checkout.commit.stepPayment')
    : t('billing.page.checkout.commit.title'),
)

/** Who the invoice will name. Read from the session, never from the form. */
const commitCustomerName = computed(() => {
  const client = userStore.getUser as any
  const full = [client?.firstname, client?.lastname].filter(Boolean).join(' ').trim()
  return full || null
})
const commitCustomerEmail = computed(
  () => ((userStore.getUser as any)?.email as string | undefined) ?? null,
)

/**
 * The address the page's card collected. The flow owns the machine; this is
 * only what it starts from, so a customer who reopens the sheet is not asked
 * for it again.
 */
const identityEmail = ref('')

/**
 * How far through the signup wizard, folded into the sheet's own rail.
 *
 * The wizard used to draw a second step rail of its own, so the sheet showed
 * two stacked step components — and the one describing the purchase was the
 * one that mattered least while somebody was filling in an address. One rail
 * now, with the sub-count on the chip they are standing on.
 */
const identitySubProgress = ref<{ index: number; total: number } | null>(null)

/**
 * Open the sheet on the identity step, with the address already in hand.
 *
 * Opening BEFORE `check-email` has answered is deliberate. The card used to
 * spin a small button at the bottom of the page and then reveal the branch
 * BELOW the fold, so the customer's click produced no visible response at all.
 * Now the click produces the sheet, and the answer arrives inside it.
 */
function startIdentity(email: string) {
  identityEmail.value = email
  openCommitAt('identity')
}

function openCommitAt(step: CommitStep) {
  if (trialActive.value && step === 'review') trialAccepted.value = false
  orderError.value = null
  commitStep.value = step
  commitOpen.value = true
}

function continueOrder() {
  if (!canContinueOrder.value) return scrollToFirstBlocker()
  if (trialActive.value) return openCommitAt('review')
  return requiresTopupState.value ? retryAfterTopup() : placeOrder()
}

/**
 * Closing has to put focus somewhere real.
 *
 * The identity card unmounts the instant a session exists, so by the time this
 * sheet closes the element the overlay would restore focus to is detached —
 * and focus falls back to `<body>`, which leaves a keyboard user at the top of
 * a very long page with no idea where they were. The CTA is where they were
 * heading, so that is where focus goes.
 */
function closeCommit() {
  commitOpen.value = false
  // On the payment step this IS the payment panel, and the poll is keyed on
  // `paymentWaitOpen` — leaving it set would poll behind a closed sheet.
  if (commitStep.value === 'payment') paymentWaitOpen.value = false
  void nextTick(() => {
    const el = reviewButton.value?.$el ?? reviewButton.value
    if (el && typeof el.focus === 'function') el.focus()
  })
}

/** A "change this" link from inside the sheet: close, then land on the step. */
function editFromCommit(anchor: string) {
  commitOpen.value = false
  void nextTick(() => progress.goTo(anchor))
}
</script>

<template>
  <div class="p-4 pb-24 lg:pb-4">
    <div v-if="loadingCatalog" class="text-sm text-muted">
      {{ $t('billing.page.checkout.loadingCatalog') }}
    </div>
    <div v-else-if="!billing.catalog" class="text-sm text-red-500">
      {{ $t('billing.page.checkout.catalogError') }}
    </div>

    <UAlert
      v-else-if="trialActive && trialStatusMessage"
      icon="i-lucide-gift"
      :title="t('trial.title')"
      :description="trialStatusMessage"
    >
      <template #actions>
        <UButton :to="paidCheckoutLocation" :label="t('trial.paidCheckout')" variant="outline" />
      </template>
    </UAlert>
    <div v-else class="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
      <UAlert
        v-if="trialActive"
        class="lg:col-span-12"
        icon="i-lucide-gift"
        :title="t('trial.zeroToday', { zero: billing.formatCents('0') })"
        :description="t('trial.configureHint')"
      >
        <template #actions>
          <UButton :to="paidCheckoutLocation" :label="t('trial.paidCheckout')" variant="outline" />
        </template>
      </UAlert>
      <UAlert
        v-if="trialAccountMismatch"
        class="lg:col-span-12"
        color="warning"
        :title="t('trial.accountMismatch')"
        ><template #actions
          ><UButton :label="t('trial.switchAccount')" @click="switchTrialAccount" /></template
      ></UAlert>
      <section
        class="relative overflow-hidden rounded-2xl border border-primary/25 bg-primary/5 p-4 dark:bg-primary/10 sm:p-6 lg:col-span-12"
        aria-labelledby="checkout-intro-title"
      >
        <div
          class="pointer-events-none absolute -right-16 -top-20 size-56 rounded-full bg-primary/10 blur-3xl"
          aria-hidden="true"
        />
        <div class="relative max-w-3xl">
          <div class="flex items-center gap-3">
            <span
              class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-inverted shadow-sm"
              aria-hidden="true"
            >
              <UIcon name="i-lucide-server-cog" class="size-5" />
            </span>
            <div class="min-w-0">
              <h2
                id="checkout-intro-title"
                class="text-lg font-bold tracking-tight text-highlighted sm:text-xl"
              >
                {{ $t('billing.page.checkout.intro.title') }}
              </h2>
            </div>
          </div>
          <p class="mt-3 max-w-2xl text-sm leading-relaxed text-toned sm:ml-14 sm:mt-1">
            {{ $t('billing.page.checkout.intro.description') }}
          </p>
          <ul class="mt-4 hidden flex-wrap gap-x-5 gap-y-2 text-xs text-toned sm:flex">
            <li class="flex items-center gap-1.5">
              <UIcon name="i-lucide-receipt-text" class="size-4 text-primary" />
              {{ $t('billing.page.checkout.intro.clearPrice') }}
            </li>
            <li class="flex items-center gap-1.5">
              <UIcon name="i-lucide-sliders-horizontal" class="size-4 text-primary" />
              {{ $t('billing.page.checkout.intro.flexible') }}
            </li>
            <li class="flex items-center gap-1.5">
              <UIcon name="i-lucide-shield-check" class="size-4 text-primary" />
              {{ $t('billing.page.checkout.intro.secure') }}
            </li>
          </ul>
        </div>
      </section>

      <!-- ── Main column ──────────────────────────────────── -->
      <div class="lg:col-span-8 space-y-5">
        <!-- Came back through something that reloaded the page — a social
           sign-in, a two-factor hand-off, or an ordinary refresh. Said out
           loud, because a configuration that reappears with no explanation
           reads as a bug. -->
        <UAlert
          v-if="restoredFromProvider"
          role="status"
          icon="i-lucide-history"
          color="info"
          variant="soft"
          :title="$t('billing.page.checkout.autoAdjust.restored')"
          :description="
            passwordNeedsReentry
              ? $t('billing.page.checkout.autoAdjust.passwordNotKept')
              : undefined
          "
        />

        <!-- Arrived from the marketplace on the commercial website, which had
           already asked what they wanted to run. Announced for the same reason
           the restore above is: a page that opens with choices already made has
           to say who made them. -->
        <UAlert
          v-if="preselectedFromSite"
          role="status"
          icon="i-lucide-globe"
          color="info"
          variant="soft"
          :title="
            preselectedFromSite.templateName
              ? $t('billing.page.checkout.preselect.title', {
                  name: preselectedFromSite.templateName,
                })
              : $t('billing.page.checkout.preselect.titlePlanOnly', {
                  plan: preselectedFromSite.planName,
                })
          "
          :description="
            preselectedFromSite.planTooSmall
              ? $t('billing.page.checkout.preselect.planTooSmall', {
                  name: preselectedFromSite.templateName,
                })
              : preselectedFromSite.planName
                ? $t('billing.page.checkout.preselect.withPlan')
                : $t('billing.page.checkout.preselect.templateOnly', {
                    name: preselectedFromSite.templateName,
                  })
          "
        />

        <!-- Server location -->
        <CheckoutStepCard
          id="checkout-location"
          :step="stepNumbers.location"
          :title="$t('billing.page.checkout.steps.location')"
          :subtitle="$t('billing.page.checkout.steps.locationHint')"
          :done="datacenterDone"
        >
          <CheckoutLocationPicker
            v-model="selectedDatacenterId"
            :datacenters="datacenters"
            :loading="loadingDcs"
          />
        </CheckoutStepCard>

        <!-- The image comes BEFORE the plan, and before storage.
           Before the plan: the image decides what the plan must hold. Asked
           after it, an app's RAM floor surfaced as a refusal once a plan was
           already picked; asked first, the plan table dims whatever cannot run
           the image before anyone clicks it — the order every major cloud
           uses. Before storage: the image decides the disk FLOOR, and asked
           second, picking Windows silently rewrote a disk the customer had
           already sized, off-screen, moving the price with it. -->
        <CheckoutStepCard
          v-if="selectedDatacenterId"
          id="checkout-os"
          :step="stepNumbers.os"
          :title="$t('billing.page.checkout.steps.os')"
          :subtitle="$t('billing.page.checkout.os.description')"
          :done="osDone"
        >
          <CheckoutTemplatePicker
            v-model="selectedTemplateCode"
            v-model:source="osTab"
            v-model:client-image-id="selectedClientImageId"
            :templates="billing.catalog.templates"
            :client-images="clientImageOptions"
            :plan-vcpu="selectedOffering?.vcpu ?? null"
            :plan-ram-mb="selectedOffering?.ram_mb ?? null"
            :main-disk-gb="mainDiskTotalGb"
            :remedy-label="$t('billing.page.checkout.os.requirementAlert.action')"
            surface="checkout"
            @remedy="scrollToPlan"
          />

          <CheckoutAddonGroup
            :addons="addonsForStep('os')"
            :quantities="addonQuantities"
            :configurator="configurator"
          />
        </CheckoutStepCard>

        <!-- Billing cycle: its own step, because it reprices everything
           below it and its discounts are the whole reason to commit. -->
        <CheckoutStepCard
          v-if="selectedDatacenterId"
          id="checkout-cycle"
          :step="stepNumbers.cycle"
          :title="$t('billing.page.checkout.steps.cycle')"
          :subtitle="$t('billing.page.checkout.steps.cycleHint')"
          done
        >
          <CheckoutTermPicker
            v-model="selectedTerm"
            :terms="billing.catalog.terms"
            :offering="selectedOffering"
            :offerings="computeOfferings"
            :locked="trialActive || selectedSubscriptionId !== 'new'"
          />
          <UFormField
            v-if="isNewOnDemand"
            :label="$t('billing.page.checkout.recharge.title')"
            :description="$t('billing.page.checkout.recharge.description')"
            :error="initialRechargeError ?? undefined"
            name="initial-recharge"
            class="mt-4"
          >
            <UInput
              v-model="initialRechargeAmount"
              data-testid="checkout-initial-recharge"
              inputmode="decimal"
              autocomplete="off"
              class="w-full sm:w-48"
            />
            <p v-if="initialRechargeMinimumCents" class="mt-1 text-xs text-muted">
              {{
                $t('billing.page.checkout.recharge.minimum', {
                  amount: billing.formatCents(initialRechargeMinimumCents),
                })
              }}
            </p>
          </UFormField>
        </CheckoutStepCard>

        <!-- Plan -->
        <CheckoutStepCard
          v-if="selectedDatacenterId"
          id="checkout-plan"
          :step="stepNumbers.plan"
          :title="$t('billing.page.checkout.steps.plan')"
          :subtitle="
            $t('billing.page.checkout.steps.planHint', {
              cycle: termDisplayName,
            })
          "
          :done="planDone"
          :attention="planBelowOsMin"
          flush-body
        >
          <template #badge>
            <UBadge
              v-if="selectedSubscriptionId !== 'new'"
              color="neutral"
              variant="soft"
              icon="i-lucide-lock"
              size="sm"
            >
              {{ termDisplayName }}
            </UBadge>
          </template>

          <div
            class="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-muted border-b border-default font-medium"
          >
            <!-- Narrow screens keep only plan/memory/price; the rest
               doesn't fit 390px without columns colliding. -->
            <div class="col-span-6 sm:col-span-4">
              {{ $t('billing.page.checkout.plan.colPlan') }}
            </div>
            <div class="hidden sm:block sm:col-span-1 text-right">
              {{ $t('billing.page.checkout.plan.colVcpu') }}
            </div>
            <div class="col-span-2 sm:col-span-2 text-right">
              {{ $t('billing.page.checkout.plan.colMemory') }}
            </div>
            <div class="hidden sm:block sm:col-span-2 text-right">
              {{ $t('billing.page.checkout.plan.colDisk') }}
            </div>
            <div class="col-span-4 sm:col-span-3 text-right">
              {{ $t('billing.page.checkout.plan.colPrice') }}
            </div>
          </div>

          <button
            v-for="o in computeOfferings"
            :key="o.id"
            type="button"
            :aria-pressed="selectedOfferingCode === o.code"
            :data-dimmed="planDimmed(o) ? 'true' : undefined"
            :class="[
              'relative grid w-full grid-cols-12 gap-2 border-b border-default px-4 py-3 text-left text-sm transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
              selectedOfferingCode === o.code
                ? 'bg-primary/10 on-tint dark:bg-primary/15'
                : 'hover:bg-elevated/50',
            ]"
            @click="selectedOfferingCode = o.code"
          >
            <!-- Accent bar instead of a ring: a ring on a full-width row in
               a scrolling table double-draws against its neighbours. -->
            <span
              v-if="selectedOfferingCode === o.code"
              class="absolute inset-y-0 left-0 w-1 bg-primary"
            />
            <div class="col-span-6 sm:col-span-4 flex items-center gap-2">
              <UIcon
                :name="
                  selectedOfferingCode === o.code ? 'i-lucide-check-circle' : 'i-lucide-circle'
                "
                :class="
                  selectedOfferingCode === o.code
                    ? 'text-primary size-5 shrink-0'
                    : 'text-muted size-5 shrink-0'
                "
              />
              <div class="min-w-0">
                <div :class="['font-semibold', planMainTone(o)]">{{ o.name }}</div>
                <!-- Specs hidden as columns on narrow screens resurface here -->
                <div class="sm:hidden text-xs text-muted">
                  {{ o.vcpu }} vCPU · {{ o.main_disk_base_gb }} GB+
                </div>
                <div class="text-xs text-muted">
                  {{
                    $t('billing.page.checkout.plan.includedIps', {
                      ipv4: o.included_ipv4_per_vm,
                      ipv6: o.included_ipv6_per_vm,
                    })
                  }}
                </div>
              </div>
            </div>
            <div :class="['hidden sm:block sm:col-span-1 text-right', planMainTone(o)]">
              {{ o.vcpu }}
            </div>
            <div :class="['col-span-2 sm:col-span-2 text-right', planMainTone(o)]">
              {{ (o.ram_mb / 1024).toFixed(o.ram_mb % 1024 ? 1 : 0) }} GB
            </div>
            <!-- The disk column is where "you can't change this" was being
               read, so the expandability lives right in the cell. -->
            <div :class="['hidden sm:block sm:col-span-2 text-right', planMainTone(o)]">
              <div>{{ o.main_disk_base_gb }} GB</div>
              <div v-if="trialAllows('MAIN_DISK_EXPANSION')" class="text-[11px] text-primary">
                {{ $t('billing.page.checkout.plan.diskExpandable') }}
              </div>
            </div>
            <div :class="['col-span-4 sm:col-span-3 text-right leading-tight', planMainTone(o)]">
              <div v-if="planReferencePrice(o)" class="text-[11px] text-muted line-through">
                {{ planReferencePrice(o) }}
              </div>
              <div class="font-semibold tabular-nums">
                {{ planMonthlyPrice(o)
                }}<span class="text-[11px] font-normal text-muted">{{
                  $t('billing.page.checkout.term.perMonth')
                }}</span>
              </div>
              <!-- No discount badge here: the struck reference already says
                 it, and the same percentage on 13 rows is just noise. -->
              <div class="text-[11px] text-muted">{{ planHourlyPrice(o) }}/h</div>
            </div>
            <!-- Under the whole row, not inside the plan cell: on a phone that
               cell is half the width and the reason wrapped to three lines.
               A dark shade because plain text-warning is ~2:1 on white, and
               the selected row's tint costs another half point. -->
            <div
              v-if="selectedTemplate && !offeringMeetsOsMin(o)"
              class="col-span-12 -mt-1 flex items-start gap-1 pl-7 text-xs text-warning-800 dark:text-warning-400"
            >
              <UIcon name="i-lucide-alert-triangle" class="mt-0.5 size-3 shrink-0" />
              {{
                $t('billing.page.checkout.plan.belowMinSpec', {
                  spec: planShortfallLabel(o),
                  name: selectedTemplate.name,
                })
              }}
            </div>
          </button>

          <!-- Nobody scrolls past a plan table wondering about storage if
             the table itself says the disk is a dial. -->
          <div
            class="flex items-center justify-between gap-3 px-4 py-3 text-xs text-muted flex-wrap"
          >
            <span class="flex items-center gap-1.5">
              <UIcon name="i-lucide-hard-drive" class="size-3.5 text-primary" />
              {{
                trialAllows('MAIN_DISK_EXPANSION') || trialAllows('EXTRA_DISK')
                  ? $t('billing.page.checkout.plan.storageNote')
                  : t('trial.baseOnly')
              }}
            </span>
            <UButton
              v-if="
                selectedOffering &&
                (trialAllows('MAIN_DISK_EXPANSION') || trialAllows('EXTRA_DISK'))
              "
              size="xs"
              variant="soft"
              color="primary"
              icon="i-lucide-arrow-down"
              :label="$t('billing.page.checkout.plan.adjustStorage')"
              @click="scrollToStorage"
            />
          </div>
        </CheckoutStepCard>

        <!-- Storage: still directly under the machine it belongs to, now
           with the OS floor already established. -->
        <CheckoutStepCard
          v-if="selectedOffering"
          id="checkout-storage"
          :step="stepNumbers.storage"
          :title="$t('billing.page.checkout.steps.disks')"
          :subtitle="
            trialAllows('MAIN_DISK_EXPANSION') || trialAllows('EXTRA_DISK')
              ? $t('billing.page.checkout.steps.disksHint')
              : t('trial.baseOnly')
          "
          done
        >
          <CheckoutAutoAdjustments :items="autoAdjustments" class="mb-4" />

          <CheckoutStorageStep
            :allow-main-expansion="trialAllows('MAIN_DISK_EXPANSION')"
            :allow-extra-disks="trialAllows('EXTRA_DISK')"
            v-model:main-extra-gb="mainDiskExtraGb"
            v-model:extra-disks="extraDisks"
            :base-gb="baseDiskGb"
            :step-gb="diskStep"
            :min-extra-gb="minExtraDiskGb"
            :min-main-extra-gb="minMainExtraGb"
            :os-name="osDisplayName"
            :os-min-disk-gb="osMinDiskGb"
            :term="checkoutTerm"
          />

          <CheckoutAddonGroup
            :addons="addonsForStep('storage')"
            :quantities="addonQuantities"
            :configurator="configurator"
          />
        </CheckoutStepCard>

        <!-- Password -->
        <CheckoutStepCard
          v-if="selectedOffering && requiresPassword"
          id="checkout-password"
          :step="stepNumbers.password!"
          :title="$t('billing.page.checkout.steps.password')"
          :done="passwordDone"
        >
          <div class="space-y-4">
            <p class="text-sm text-muted">
              {{ $t('billing.page.checkout.password.description') }}
            </p>
            <div class="space-y-1">
              <label class="text-sm font-medium">{{
                $t('billing.page.checkout.password.label')
              }}</label>
              <UInput
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('billing.page.checkout.password.placeholder')"
                autocomplete="new-password"
                class="w-full"
              >
                <template #trailing>
                  <UButton
                    variant="ghost"
                    size="xs"
                    :icon="showPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'"
                    :aria-label="
                      showPassword
                        ? $t('billing.page.checkout.password.hide')
                        : $t('billing.page.checkout.password.show')
                    "
                    @click="showPassword = !showPassword"
                  />
                </template>
              </UInput>
            </div>
            <!-- Meter is flavour; the checklist is the contract. Both are
               driven by ~/utils/password, same as account signup. Hidden
               while a character is refused: "Boa" above "Não permitido"
               is two answers to one question. -->
            <div v-if="password && passwordCharsetValid" class="space-y-2">
              <div class="flex items-center justify-between text-xs">
                <span class="text-muted">{{
                  $t('billing.page.checkout.password.strengthLabel')
                }}</span>
                <span
                  class="font-semibold"
                  :class="STRENGTH_TEXT[passwordMeter.level]"
                  aria-live="polite"
                >
                  {{ $t(`auth.password.${passwordMeter.level}`) }}
                </span>
              </div>
              <div class="h-1.5 w-full rounded-full bg-elevated overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :class="STRENGTH_BAR[passwordMeter.level]"
                  :style="{ width: `${passwordMeter.percentage}%` }"
                />
              </div>
            </div>

            <UiPasswordRequirements :password="password" :columns="2" policy="vm" />
            <div class="space-y-1">
              <label class="text-sm font-medium">{{
                $t('billing.page.checkout.password.confirmLabel')
              }}</label>
              <UInput
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :placeholder="$t('billing.page.checkout.password.confirmPlaceholder')"
                autocomplete="new-password"
                class="w-full"
              >
                <template #trailing>
                  <UButton
                    variant="ghost"
                    size="xs"
                    :icon="showConfirmPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'"
                    :aria-label="
                      showConfirmPassword
                        ? $t('billing.page.checkout.password.hide')
                        : $t('billing.page.checkout.password.show')
                    "
                    @click="showConfirmPassword = !showConfirmPassword"
                  />
                </template>
              </UInput>
              <div v-if="confirmPassword" class="flex items-center gap-1 text-xs mt-1">
                <UIcon
                  :name="
                    passwordsMatch ? 'i-heroicons-check-circle-solid' : 'i-heroicons-x-circle-solid'
                  "
                  :class="passwordsMatch ? 'text-green-600' : 'text-red-600'"
                  class="size-4"
                />
                <span :class="passwordsMatch ? 'text-green-600' : 'text-red-600'">{{
                  passwordsMatch
                    ? $t('billing.page.checkout.password.match')
                    : $t('billing.page.checkout.password.noMatch')
                }}</span>
              </div>
            </div>
          </div>
        </CheckoutStepCard>

        <!-- Conectividade -->
        <CheckoutStepCard
          v-if="selectedOffering"
          id="checkout-connectivity"
          :step="stepNumbers.connectivity"
          :title="$t('billing.page.checkout.steps.connectivity')"
          :subtitle="$t('billing.page.checkout.steps.connectivityHint')"
          :done="connectivityDone"
        >
          <div
            class="grid grid-cols-1 gap-3 md:grid-cols-2"
            role="radiogroup"
            :aria-label="$t('billing.page.checkout.steps.connectivity')"
          >
            <button
              type="button"
              role="radio"
              :aria-checked="connectivityMode === 'public'"
              :class="[
                'rounded-xl border-2 p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]',
                connectivityMode === 'public'
                  ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
                  : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
              ]"
              @click="connectivityMode = 'public'"
            >
              <div class="flex items-start gap-2">
                <UIcon name="i-lucide-globe" class="text-primary size-5 shrink-0 mt-0.5" />
                <div>
                  <div class="font-medium text-sm">
                    {{ $t('billing.page.checkout.connectivity.public') }}
                  </div>
                  <div class="mt-0.5 text-sm text-toned">
                    {{ $t('billing.page.checkout.connectivity.publicDesc') }}
                  </div>
                </div>
              </div>
            </button>
            <button
              type="button"
              role="radio"
              :aria-checked="connectivityMode === 'private'"
              :class="[
                'rounded-xl border-2 p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]',
                connectivityMode === 'private'
                  ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
                  : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
              ]"
              @click="connectivityMode = 'private'"
            >
              <div class="flex items-start gap-2">
                <UIcon name="i-lucide-lock" class="text-primary size-5 shrink-0 mt-0.5" />
                <div>
                  <div class="font-medium text-sm">
                    {{ $t('billing.page.checkout.connectivity.private') }}
                  </div>
                  <div class="mt-0.5 text-sm text-toned">
                    {{ $t('billing.page.checkout.connectivity.privateDesc') }}
                  </div>
                  <!-- Nobody can create a private network from inside a
                     checkout, and a new customer has none — so choosing this
                     would be a requirement they cannot satisfy here. Say up
                     front that it is something to set up afterwards, rather
                     than let them pick it and meet a blocker. -->
                  <div v-if="vpcsInDatacenter.length === 0" class="mt-1.5 text-xs text-muted">
                    {{ $t('billing.page.checkout.connectivity.noVpcYet') }}
                  </div>
                </div>
              </div>
            </button>
          </div>
          <div
            v-if="vpcsInDatacenter.length > 0 || connectivityMode === 'private'"
            class="mt-4 space-y-2"
          >
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium"
                >{{ $t('billing.page.checkout.connectivity.vpcLabel') }}
                <span v-if="connectivityMode === 'private'" class="text-red-500">*</span
                ><span v-else class="text-muted text-xs">{{
                  $t('billing.page.checkout.connectivity.vpcOptional')
                }}</span></label
              >
              <UButton
                size="xs"
                variant="ghost"
                icon="i-lucide-refresh-cw"
                :loading="loadingVpcs"
                :aria-label="$t('billing.page.checkout.connectivity.refreshVpcs')"
                @click="fetchVpcs"
              />
            </div>
            <USelect
              v-model="selectedVpcId"
              class="w-full"
              :items="[
                ...(connectivityMode === 'public'
                  ? [{ label: $t('billing.page.checkout.connectivity.noVpc2'), value: null }]
                  : []),
                ...vpcsInDatacenter.map((v) => ({
                  label: v.name + (v.cidr ? ` (${v.cidr})` : ''),
                  value: v.id,
                })),
              ]"
              :placeholder="
                vpcsInDatacenter.length === 0
                  ? $t('billing.page.checkout.connectivity.noVpc')
                  : $t('billing.page.checkout.connectivity.selectVpc')
              "
              :disabled="vpcsInDatacenter.length === 0"
            />
            <p
              v-if="connectivityMode === 'private' && !selectedVpcId"
              class="text-xs"
              :class="vpcsInDatacenter.length === 0 ? 'text-toned' : 'text-red-500'"
            >
              {{
                vpcsInDatacenter.length === 0
                  ? $t('billing.page.checkout.connectivity.noVpcYet')
                  : $t('billing.page.checkout.connectivity.vpcRequired')
              }}
            </p>
          </div>
          <UAlert
            v-if="connectivityMode === 'private'"
            icon="i-lucide-shield-check"
            color="info"
            variant="soft"
            class="mt-4"
            :title="$t('billing.page.checkout.connectivity.vpnAlert')"
            :description="$t('billing.page.checkout.connectivity.vpnAlertDesc')"
          >
            <template #actions>
              <UButton
                to="/vpn-gateway"
                target="_blank"
                size="xs"
                color="info"
                variant="outline"
                icon="i-lucide-arrow-up-right"
                :label="$t('billing.page.checkout.connectivity.vpnLink')"
              />
            </template>
          </UAlert>

          <CheckoutAddonGroup
            :addons="addonsForStep('connectivity')"
            :quantities="addonQuantities"
            :configurator="configurator"
          />
        </CheckoutStepCard>

        <!-- Whatever has no step of its own.
           Every add-on the mapping knows about now sits beside the
           decision it modifies; this card is the fallback bucket, and it
           exists so a newly-seeded addon_type is sellable the day an
           operator adds it rather than silently invisible. -->
        <CheckoutStepCard
          v-if="selectedOffering && looseAddons.length > 0"
          id="checkout-addons"
          :step="stepNumbers.addons!"
          :title="$t('billing.page.checkout.steps.addons')"
          :subtitle="$t('billing.page.checkout.steps.addonsHint')"
          optional
          done
        >
          <CheckoutAddonGroup
            :addons="looseAddons"
            :quantities="addonQuantities"
            :configurator="configurator"
            bare
          />
        </CheckoutStepCard>

        <!-- Billing belongs at the end of the configuration. It is only a
           step when the customer has an existing subscription, because
           otherwise "create new" is not a choice at all. -->
        <CheckoutStepCard
          v-if="selectedOffering && hasSubscriptionChoice"
          id="checkout-billing"
          :step="stepNumbers.subscription!"
          :title="$t('billing.page.checkout.steps.billing')"
          :subtitle="$t('billing.page.checkout.steps.billingHint')"
          :done="!subscriptionSelectionPending"
          :attention="subscriptionSelectionPending"
        >
          <CheckoutSubscriptionPicker
            v-model="selectedSubscriptionId"
            v-model:pending="subscriptionSelectionPending"
            :subscriptions="availableSubscriptions"
          />
        </CheckoutStepCard>

        <!-- Who is buying — last, and only while nobody is.
           This is what used to be a redirect to `/register` before a single
           price had been shown. Asked here, at the end, the customer has had
           the total on screen the whole way down and the form is what it
           actually is: the billing details for the invoice about to be
           issued. -->
        <CheckoutStepCard
          v-if="selectedOffering && !signedIn"
          id="checkout-identity"
          :step="stepNumbers.identity!"
          :title="$t('billing.page.checkout.identity.title')"
          :subtitle="$t('billing.page.checkout.identity.subtitle')"
          :done="false"
        >
          <CheckoutIdentityStep
            :blocked="configurationBlockers.length > 0"
            :blockers="configurationBlockers"
            @continue="startIdentity"
          />
        </CheckoutStepCard>
      </div>

      <!-- ── Sticky sidebar ──────────────────────────────── -->
      <div id="checkout-summary" class="lg:col-span-4 lg:self-stretch">
        <div class="lg:sticky lg:top-4">
          <UCard
            class="lg:flex lg:max-h-[calc(100dvh-9rem)] lg:flex-col"
            :ui="{
              body: 'lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain',
            }"
          >
            <template #header>
              <h2 class="text-base font-semibold">
                {{ $t('billing.page.checkout.summary.title') }}
              </h2>
            </template>

            <!-- Nothing configured yet: show what the flow will ask for,
               rather than an empty box with one grey sentence. Not "select a
               plan to start" — the image is asked for before the plan. -->
            <div v-if="!selectedOffering" class="space-y-3">
              <p class="text-sm text-muted">
                {{ $t('billing.page.checkout.summary.emptyIntro') }}
              </p>
              <ul class="space-y-2 text-sm">
                <li
                  v-for="item in [
                    { done: datacenterDone, label: $t('billing.page.checkout.steps.location') },
                    { done: osDone, label: $t('billing.page.checkout.steps.os') },
                    { done: planDone, label: $t('billing.page.checkout.steps.plan') },
                  ]"
                  :key="item.label"
                  class="flex items-center gap-2"
                  :class="item.done ? 'text-default' : 'text-muted'"
                >
                  <UIcon
                    :name="item.done ? 'i-lucide-circle-check' : 'i-lucide-circle-dashed'"
                    :class="item.done ? 'size-4 text-success' : 'size-4 text-dimmed'"
                  />
                  {{ item.label }}
                </li>
              </ul>
              <CheckoutCurrencyNote class="pt-1" />
              <p class="text-xs text-muted flex items-start gap-1.5">
                <UIcon name="i-lucide-shield-check" class="size-3.5 shrink-0 mt-px" />
                {{ $t('billing.page.checkout.summary.trust') }}
              </p>
            </div>

            <div v-else class="space-y-3 text-sm">
              <!-- What is being bought. Shared verbatim with the commit
                 sheet's review step, so the two surfaces cannot describe the
                 same order differently. -->
              <CheckoutOrderSummaryLines
                :has-subscription-choice="hasSubscriptionChoice"
                :subscription-label="subscriptionLabel"
                :location-name="selectedDatacenterName"
                :term-label="termDisplayName"
                :os-name="osDisplayName"
                :offering="selectedOffering"
                :total-storage-gb="totalStorageGb"
                @edit-storage="scrollToStorage"
              />

              <USeparator />

              <CheckoutTotals
                :trial="trialSummary"
                :quote="quote"
                :simulating="simulating"
                :simulation-error="simulationError"
                :is-new-subscription="selectedSubscriptionId === 'new'"
                :prorata-estimate="attachProrataEstimate"
                :prorata-hours="attachProrataHours"
                :term="checkoutTerm"
                :on-demand-monthly-micro-cents="onDemandMonthlyMicroCents"
                :initial-credit-cents="orderInitialCreditCents"
              />

              <template v-if="couponAllowed && quote">
                <USeparator />
                <div class="space-y-2">
                  <div class="flex items-end justify-between gap-2">
                    <UFormField
                      :label="$t('billing.page.checkout.summary.couponField')"
                      class="flex-1"
                    >
                      <UInput
                        v-model="couponCode"
                        :placeholder="$t('billing.page.checkout.summary.couponPlaceholder')"
                        icon="i-lucide-ticket-percent"
                        autocomplete="off"
                        autocapitalize="characters"
                        :disabled="placing"
                      />
                    </UFormField>
                    <UButton
                      variant="outline"
                      icon="i-lucide-refresh-cw"
                      :loading="couponLoading"
                      :disabled="!normalizedCouponCode"
                      @click="refreshCouponPreview"
                    >
                      {{ $t('billing.page.checkout.summary.applyBtn') }}
                    </UButton>
                  </div>

                  <div v-if="couponError" class="text-xs text-red-500">
                    {{ couponError }}
                  </div>
                  <!-- A code that will NOT be honoured gets one line, not a
                     panel. The panel's whole content is the arithmetic of the
                     discount, and for a rejected coupon that arithmetic is
                     three restatements of the same number — a "recurring
                     discount" of -R$ 0,00 under a total that equals the total
                     — around a name the server never sent, so "Coupon not
                     found" was titled `Coupon` above an empty code line. The
                     answer the customer needs is the reason and what it costs
                     them, which is exactly what fits on one line. -->
                  <p
                    v-else-if="couponPreview && !couponPreview.valid"
                    role="status"
                    class="text-xs text-warning"
                  >
                    {{ couponStatusText(couponPreview) }}
                    {{ $t('billing.page.checkout.couponStatus.notApplied') }}
                  </p>
                  <!-- Only ever rendered for a coupon that IS applied, so nothing
                     in here branches on validity any more. -->
                  <div
                    v-else-if="couponPreview"
                    class="rounded-md border border-default bg-elevated/40 p-3 space-y-2"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <div v-if="couponPreview.coupon?.code" class="text-xs text-muted">
                          {{ couponPreview.coupon.code }}
                        </div>
                        <div class="font-medium">
                          {{
                            couponPreview.coupon?.name ??
                            $t('billing.page.checkout.summary.couponFallbackName')
                          }}
                        </div>
                      </div>
                      <UBadge color="success" variant="soft">
                        {{ $t('billing.page.checkout.summary.couponApplied') }}
                      </UBadge>
                    </div>
                    <div class="space-y-1 text-xs">
                      <div class="flex justify-between">
                        <span class="text-muted">{{
                          $t('billing.page.checkout.summary.subscriptionTotal')
                        }}</span>
                        <span class="font-mono">{{
                          billing.formatMicroCents(couponPreview.service_total_micro_cents)
                        }}</span>
                      </div>
                      <div class="flex justify-between text-green-600">
                        <span>{{ $t('billing.page.checkout.summary.recurringDiscount') }}</span>
                        <span class="font-mono"
                          >-{{ billing.formatMicroCents(couponPreview.discount_micro_cents) }}</span
                        >
                      </div>
                      <div class="flex justify-between font-semibold">
                        <span>{{ $t('billing.page.checkout.summary.totalAfterDiscount') }}</span>
                        <span class="font-mono">{{
                          billing.formatMicroCents(couponPreview.payable_micro_cents)
                        }}</span>
                      </div>
                    </div>
                    <p class="text-xs text-muted">
                      {{ $t('billing.page.checkout.summary.couponNote') }}
                    </p>
                  </div>
                </div>
              </template>

              <!-- Balance panel for existing subscription -->
              <template v-if="selectedSubscription?.balance && quote">
                <USeparator />
                <div class="space-y-1.5 text-xs">
                  <div class="font-medium text-sm">
                    {{ $t('billing.page.checkout.summary.yourBalance') }}
                  </div>
                  <div class="flex justify-between">
                    <span class="text-muted">{{
                      $t('billing.page.checkout.summary.balanceAvailable')
                    }}</span>
                    <span class="font-mono">{{
                      billing.formatMicroCents(selectedSubscription.balance.available_micro_cents)
                    }}</span>
                  </div>
                  <div
                    v-if="BigInt(selectedSubscription.balance.temporary_micro_cents) > 0n"
                    class="flex justify-between"
                  >
                    <span class="text-muted">{{
                      $t('billing.page.checkout.summary.balanceTemporary')
                    }}</span>
                    <span class="font-mono">{{
                      billing.formatMicroCents(selectedSubscription.balance.temporary_micro_cents)
                    }}</span>
                  </div>
                  <div class="flex justify-between font-semibold">
                    <span>{{ $t('billing.page.checkout.summary.balanceUsable') }}</span>
                    <span
                      :class="
                        BigInt(selectedSubscription.balance.usable_micro_cents) >= 0n
                          ? 'text-green-600'
                          : 'text-red-600'
                      "
                    >
                      {{
                        billing.formatMicroCents(selectedSubscription.balance.usable_micro_cents)
                      }}
                    </span>
                  </div>
                  <!-- What happens on the click, said here beside the balance,
                     before it: paid from the balance; or an invoice for the
                     remainder after the balance comes off, never below the
                     minimum recharge. -->
                  <template v-if="attachShortfall != null && attachProrataEstimate">
                    <div class="flex justify-between pt-1">
                      <span class="text-muted">{{
                        $t('billing.page.checkout.summary.chargeToCycleEnd', {
                          hours: attachProrataHours ?? 0,
                        })
                      }}</span>
                      <span class="font-mono">{{
                        billing.formatMicroCents(attachProrataEstimate)
                      }}</span>
                    </div>
                    <template v-if="attachShortfall === 0n">
                      <div class="flex justify-between text-success font-semibold">
                        <span>{{ $t('billing.page.checkout.summary.paidFromBalanceNow') }}</span>
                        <span class="font-mono"
                          >-{{ billing.formatMicroCents(attachProrataEstimate) }}</span
                        >
                      </div>
                    </template>
                    <template v-else-if="attachInvoiceTotalEstimate">
                      <div
                        v-if="attachBalanceAppliedEstimate"
                        class="flex justify-between text-success"
                      >
                        <span>{{
                          $t('billing.page.checkout.summary.balanceAppliedEstimate')
                        }}</span>
                        <span class="font-mono"
                          >-{{ billing.formatMicroCents(attachBalanceAppliedEstimate) }}</span
                        >
                      </div>
                      <div v-if="attachMinimumRechargeCreditEstimate" class="flex justify-between">
                        <span class="text-muted">{{
                          $t('billing.page.checkout.summary.minimumRechargeEstimate')
                        }}</span>
                        <span class="font-mono"
                          >+{{
                            billing.formatMicroCents(attachMinimumRechargeCreditEstimate)
                          }}</span
                        >
                      </div>
                      <div class="flex justify-between font-semibold">
                        <span>{{ $t('billing.page.checkout.summary.invoiceEstimate') }}</span>
                        <span class="font-mono">{{
                          billing.formatMicroCents(attachInvoiceTotalEstimate)
                        }}</span>
                      </div>
                      <p
                        v-if="attachBalanceAppliedEstimate"
                        class="text-[11px] text-muted leading-snug pt-1"
                      >
                        {{ $t('billing.page.checkout.summary.balanceAppliedExplainer') }}
                      </p>
                      <p
                        v-if="attachMinimumRechargeCreditEstimate"
                        class="text-[11px] text-muted leading-snug pt-1"
                      >
                        {{
                          $t('billing.page.checkout.summary.minimumRechargeExplainer', {
                            min: billing.formatMicroCents(
                              attachMinimumRechargeMicroCents.toString(),
                            ),
                            amount: billing.formatMicroCents(attachMinimumRechargeCreditEstimate),
                          })
                        }}
                      </p>
                    </template>
                    <p
                      v-if="
                        quote?.total_cycle_micro_cents && selectedSubscription.current_cycle_ends_at
                      "
                      class="text-[11px] text-muted leading-snug pt-1"
                    >
                      {{
                        $t('billing.page.checkout.summary.renewsAt', {
                          amount: billing.formatMicroCents(quote.total_cycle_micro_cents),
                          date: new Date(
                            selectedSubscription.current_cycle_ends_at,
                          ).toLocaleDateString(),
                        })
                      }}
                    </p>
                  </template>
                </div>
              </template>

              <!-- Requires topup state -->
              <template v-if="requiresTopupState">
                <USeparator />
                <UAlert
                  icon="i-lucide-triangle-alert"
                  color="warning"
                  variant="soft"
                  :title="$t('billing.page.checkout.summary.insufficientBalance')"
                >
                  <template #description>
                    <div class="space-y-2 text-xs">
                      <div class="flex justify-between">
                        <span>{{ $t('billing.page.checkout.summary.estimatedCost') }}</span>
                        <strong>{{ billing.formatCents(requiresTopupState.requiredCents) }}</strong>
                      </div>
                      <div class="flex justify-between">
                        <span>{{ $t('billing.page.checkout.summary.usableBalance') }}</span>
                        <strong>{{ billing.formatCents(requiresTopupState.usableCents) }}</strong>
                      </div>
                      <div class="flex justify-between text-red-600 font-semibold">
                        <span>{{ $t('billing.page.checkout.summary.shortfall') }}</span>
                        <strong>{{
                          billing.formatCents(requiresTopupState.shortfallCents)
                        }}</strong>
                      </div>
                      <div
                        v-if="topupChargeCents > Number(requiresTopupState.shortfallCents)"
                        class="flex justify-between"
                      >
                        <span>{{ $t('billing.page.checkout.summary.minimumRecharge') }}</span>
                        <strong>{{ billing.formatCents(String(topupChargeCents)) }}</strong>
                      </div>
                      <p class="text-muted mt-1">
                        {{
                          $t('billing.page.checkout.summary.rechargeNote', {
                            cta: $t('billing.page.checkout.cta.checkBalance'),
                          })
                        }}
                      </p>
                    </div>
                  </template>
                </UAlert>
                <UButton
                  block
                  size="lg"
                  color="warning"
                  icon="i-lucide-receipt"
                  :loading="topupGenerating"
                  @click="generateTopupInvoice"
                >
                  {{
                    $t('billing.page.checkout.summary.generateRecharge', {
                      amount: billing.formatCents(String(topupChargeCents)),
                    })
                  }}
                </UButton>
              </template>

              <!-- Pending invoice: the detail lives in the waiting panel,
                 so the summary only has to lead back to it. -->
              <template v-if="pendingInvoice">
                <USeparator />
                <UAlert
                  icon="i-lucide-clock"
                  color="info"
                  variant="soft"
                  :title="$t('billing.page.checkout.summary.awaitingPayment')"
                  :description="
                    $t('billing.page.checkout.summary.awaitingPaymentDesc', {
                      id: pendingInvoice.invoiceExternalId,
                    })
                  "
                />
                <div class="rounded-md border border-default bg-elevated/40 px-3 py-2 space-y-1">
                  <div
                    v-if="
                      pendingInvoice.balanceAppliedCents &&
                      BigInt(pendingInvoice.balanceAppliedCents) > 0n
                    "
                    class="flex justify-between text-xs text-success"
                  >
                    <span>{{ $t('billing.page.checkout.summary.balanceApplied') }}</span>
                    <span class="font-mono"
                      >-{{ billing.formatCents(pendingInvoice.balanceAppliedCents) }}</span
                    >
                  </div>
                  <div
                    v-if="
                      pendingInvoice.minimumRechargeCreditCents &&
                      BigInt(pendingInvoice.minimumRechargeCreditCents) > 0n
                    "
                    class="flex justify-between text-xs text-muted"
                  >
                    <span>{{ $t('billing.page.checkout.summary.minimumRechargeCredit') }}</span>
                    <span class="font-mono"
                      >+{{ billing.formatCents(pendingInvoice.minimumRechargeCreditCents) }}</span
                    >
                  </div>
                  <div class="flex justify-between text-sm font-semibold">
                    <span>{{ $t('billing.page.checkout.summary.invoiceAmount') }}</span>
                    <span class="font-mono">{{
                      billing.formatCents(pendingInvoice.totalCents)
                    }}</span>
                  </div>
                </div>
                <UButton
                  block
                  size="lg"
                  color="primary"
                  icon="i-lucide-hourglass"
                  @click="reopenPaymentWait"
                >
                  {{ $t('billing.page.checkout.summary.trackPayment') }}
                </UButton>
              </template>

              <!-- Why the CTA is disabled, spelled out -->
              <CheckoutOrderBlockers
                v-if="!pendingInvoice"
                id="checkout-blockers"
                :blockers="pageOrderBlockers"
              />

              <!-- Main CTA.
                 `aria-disabled`, not `disabled`: a truly disabled button
                 leaves the tab order and announces nothing, so a keyboard
                 user tabs straight past the one control they came for and
                 is told nothing about why. This one stays focusable and
                 points at the blocker list, which already exists so the
                 button and its reasons cannot disagree.

                 `:loading` is bound to `placing` alone — a loading
                 UButton IS a disabled button, so binding it to anything
                 wider would silently swallow Enter and the click. -->
              <UButton
                v-if="!pendingInvoice"
                id="checkout-cta"
                ref="reviewButton"
                data-testid="checkout-place-order"
                block
                size="lg"
                color="primary"
                class="mt-3"
                :loading="placing"
                :aria-disabled="!canContinueOrder"
                :aria-describedby="canContinueOrder ? undefined : 'checkout-blockers'"
                :icon="ctaIcon"
                @click="continueOrder"
              >
                {{ ctaLabel }}
              </UButton>

              <!-- Paid orders commit from this summary; trials open the terms
                 and consent in the review sheet before they can be started.
                 ONE button, deliberately.
                 There was a "Review and confirm" here beside the CTA, and two
                 buttons that both end in a purchase is one too many: the
                 customer has to work out which of them buys the thing. It was
                 also redundant on its own terms — this sidebar IS the review
                 (it lists the location, the cycle, the OS, the machine, every
                 priced line, the total and whatever is still missing), so a
                 button offering to show them that again was offering the
                 screen they were already reading.
                 The commit sheet keeps its review STEP, because there it is
                 the only thing on screen: it covers the page during signup, so
                 the customer cannot see this sidebar at the moment they
                 confirm. -->
              <p class="text-xs text-muted text-center">
                <span v-if="trialActive">{{ t('trial.startFooter') }}</span>
                <span v-else-if="selectedSubscriptionId === 'new'">{{
                  $t('billing.page.checkout.summary.newSubscriptionFooter')
                }}</span>
                <span v-else>{{
                  $t('billing.page.checkout.summary.existingSubscriptionFooter')
                }}</span>
              </p>
              <CheckoutCurrencyNote class="justify-center" />
              <p class="text-xs text-muted text-center flex items-center justify-center gap-1">
                <UIcon name="i-lucide-shield-check" class="size-3.5" />
                {{ $t('billing.page.checkout.summary.trust') }}
              </p>
            </div>
          </UCard>
        </div>
      </div>
    </div>

    <!-- Mobile: total + CTA always in reach. Hidden while a pending
       invoice is displayed — that state lives in the summary card. -->
    <CheckoutMobileBar
      v-if="billing.catalog && selectedOffering && !pendingInvoice"
      :trial="trialSummary"
      :quote="quote"
      :simulating="simulating"
      :simulation-error="simulationError"
      :is-new-subscription="selectedSubscriptionId === 'new'"
      :prorata-estimate="attachProrataEstimate"
      :prorata-hours="attachProrataHours"
      :can-place-order="canContinueOrder"
      :placing="placing"
      :cta-label="ctaLabel"
      :cta-icon="ctaIcon"
      :blockers="pageOrderBlockers"
      :term="checkoutTerm"
      :on-demand-monthly-micro-cents="onDemandMonthlyMicroCents"
      :initial-credit-cents="orderInitialCreditCents"
      @place-order="continueOrder"
    />

    <!-- Waiting for payment. Dismissible: the order survives, and the
       summary keeps a button back into this panel. -->
    <!-- A balance TOP-UP still gets a dialog of its own: it is reached from the
       signed-in console with no commit sheet around it. An ORDER's payment
       step is a step of that sheet, so the two never stack. -->
    <CheckoutPaymentWaitModal
      v-if="paymentWaitMode === 'topup' && pendingTopup"
      v-model:open="paymentWaitOpen"
      mode="topup"
      :invoice-external-id="pendingTopup?.invoiceExternalId ?? ''"
      :total-cents="pendingTopup?.totalCents ?? '0'"
      :gross-cents="null"
      :discount-cents="null"
      :state="paymentWaitState"
      :checking="paymentChecking"
      :last-checked-at="paymentLastCheckedAt"
      :expires-at="null"
      :error-message="paymentCheckError"
      :opening-invoice="openingInvoice"
      @check="checkPaymentNow(true)"
      @open-invoice="openInvoicePage"
    />

    <!-- ── Where the purchase is actually made ─────────────────────────────
       Identity, the second factor, account selection, review and waiting for
       payment, on one surface that covers the page instead of running off the
       bottom of it. Not a navigation: the configuration behind it stays
       mounted and dismissing costs nothing. -->
    <!-- `pendingInvoice` on its own is enough to render this. Placing an order
       CLEARS the draft — correctly, so the next thing configured in this tab
       does not inherit it — which means a reload afterwards has no plan
       selected. Gating on the offering alone made the restored payment panel
       impossible to show at exactly the moment it matters most: the customer
       has committed and is waiting to pay. -->
    <CheckoutCommitSheet
      v-if="(billing.catalog && selectedOffering) || pendingInvoice"
      :open="commitOpen"
      :title="commitTitle"
      :summary-line="commitSummaryLine"
      :summary-expanded="commitStep === 'review'"
      :steps="commitSteps"
      :current-step="commitIndicatorStep"
      :current-step-progress="commitStep === 'identity' ? identitySubProgress : null"
      :dismissible="!placing"
      :show-footer="commitStep === 'review'"
      @update:open="$event ? (commitOpen = true) : closeCommit()"
    >
      <!-- The order, stated once. Collapsed to a line on every step, open on
         review — where it IS the content rather than a reminder of it. -->
      <template #summary>
        <CheckoutOrderSummaryLines
          v-if="selectedOffering"
          :has-subscription-choice="hasSubscriptionChoice"
          :subscription-label="subscriptionLabel"
          :location-name="selectedDatacenterName"
          :term-label="termDisplayName"
          :os-name="osDisplayName"
          :offering="selectedOffering"
          :total-storage-gb="totalStorageGb"
          editable
          @edit-step="editFromCommit"
          @edit-storage="editFromCommit('checkout-storage')"
        />
        <USeparator class="my-3" />
        <CheckoutTotals
          :trial="trialSummary"
          :quote="quote"
          :simulating="simulating"
          :simulation-error="simulationError"
          :is-new-subscription="selectedSubscriptionId === 'new'"
          :prorata-estimate="attachProrataEstimate"
          :prorata-hours="attachProrataHours"
          :term="checkoutTerm"
          :on-demand-monthly-micro-cents="onDemandMonthlyMicroCents"
          :initial-credit-cents="orderInitialCreditCents"
        />
      </template>

      <CheckoutIdentityFlow
        v-if="commitStep === 'identity'"
        :initial-email="identityEmail"
        :social-params="socialParams"
        @complete="onIdentityComplete"
        @social-handled="socialParams = null"
        @sub-progress="identitySubProgress = $event"
      />

      <CheckoutConfirmReview
        v-else-if="commitStep === 'review'"
        v-model:password="password"
        v-model:confirm-password="confirmPassword"
        v-model:coupon-code="couponCode"
        :customer-name="commitCustomerName"
        :customer-email="commitCustomerEmail"
        :trial-description="trialActive ? trialDescription : null"
        :blockers="commitBlockers"
        :requires-password="requiresPassword"
        :password-done="passwordDone"
        :coupon-allowed="couponAllowed"
        :coupon-blocked="couponBlocked"
        :coupon-loading="couponLoading"
        :coupon-preview="couponPreview"
        :coupon-status="couponStatusText(couponPreview)"
        :order-error="orderError"
        @edit-step="editFromCommit"
        @refresh-coupon="refreshCouponPreview"
      />

      <CheckoutPaymentWaitBody
        v-else-if="commitStep === 'payment' && pendingInvoice"
        mode="order"
        :invoice-external-id="pendingInvoice.invoiceExternalId"
        :total-cents="pendingInvoice.totalCents"
        :initial-credit-cents="pendingInvoice.initialCreditCents"
        :gross-cents="pendingInvoice.grossCents"
        :discount-cents="pendingInvoice.discountCents"
        :balance-applied-cents="pendingInvoice.balanceAppliedCents"
        :minimum-recharge-credit-cents="pendingInvoice.minimumRechargeCreditCents"
        :charged-hours="pendingInvoice.chargedHours"
        :cycle-ends-at="pendingInvoice.cycleEndsAt"
        :term-cents="pendingInvoice.termCents"
        :state="paymentWaitState"
        :checking="paymentChecking"
        :last-checked-at="paymentLastCheckedAt"
        :expires-at="pendingInvoice.expiresAt"
        :error-message="paymentCheckError"
        :opening-invoice="openingInvoice"
        @close="closeCommit"
        @check="checkPaymentNow(true)"
        @open-invoice="openInvoicePage"
      />

      <template #footer>
        <div class="flex flex-col gap-2">
          <CheckoutTrialConsent
            v-if="trialActive"
            v-model="trialAccepted"
            described-by="commit-trial-terms"
            :disabled="placing"
          />
          <CheckoutOrderBlockers
            id="commit-blockers"
            class="sr-only"
            :blockers="orderBlockers"
          />
          <!-- `aria-disabled`, not `disabled`: a truly disabled button leaves the
             tab order and says nothing, so a keyboard user reaches the one
             control they came for and is told nothing about why it will not
             move. `:loading` is bound to `placing` ALONE — a loading UButton
             is a disabled one, and anything wider would swallow the click. -->
          <UButton
            data-testid="checkout-commit-confirm"
            block
            size="lg"
            color="primary"
            :loading="placing"
            :aria-disabled="!canPlaceOrder"
            :aria-describedby="canPlaceOrder ? undefined : 'commit-blockers'"
            :label="commitConfirmLabel"
            @click="canPlaceOrder ? placeOrder() : undefined"
          />
          <UButton
            block
            variant="ghost"
            color="neutral"
            :disabled="placing"
            :label="$t('billing.page.checkout.commit.keepConfiguring')"
            @click="closeCommit"
          />
        </div>
      </template>
    </CheckoutCommitSheet>
  </div>
</template>
